import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import GrocerySearch from './GrocerySearch.jsx'
import RecipesTabPanel from './components/RecipesTabPanel.jsx'
import YourPantry from './components/YourPantry.jsx'
import ChatShortcuts from './ChatShortcuts.jsx'
import ExplainabilityCard from './components/ExplainabilityCard.jsx'
import { chatMarkdownComponents } from './components/chat/chatMarkdownComponents.jsx'
import { resolveSuggestedActions } from './utils/chatSuggestedActions.js'
import { recordServingChoice } from './utils/recordServingChoice.js'
import AccountPage from './components/AccountPage.jsx'
import {
  readInitialAccountNavigation,
  APP_NAVIGATE_SETTINGS_EVENT,
  readSettingsDeepLinkHash,
} from './components/admin/adminConfigNavigation.js'
import DocumentsAdminPage from './components/admin/documents/DocumentsAdminPage.jsx'
import StoreSelector from './components/StoreSelector.jsx'
import TopNav from './components/TopNav.jsx'
import BottomTabBar from './components/BottomTabBar.jsx'
import AskTheCookFab from './components/AskTheCookFab.jsx'
import AskLockedHint from './components/AskLockedHint.jsx'
import AskTheCookLauncher from './components/AskTheCookLauncher.jsx'
import ChatDrawer from './components/chat/ChatDrawer.jsx'
import GlobalFooter from './components/GlobalFooter.jsx'
import PrivacyPolicyView from './components/PrivacyPolicyView.jsx'
import HelpModal from './components/HelpModal.jsx'
import ShopErrorBoundary from './components/ShopErrorBoundary.jsx'
import { useAuth } from './contexts/AuthContext.jsx'
import { useSelectedStore } from './contexts/useSelectedStore.js'
import LoginModal from './components/LoginModal.jsx'
import DuplicateImportModal from './DuplicateImportModal.jsx'
import { getAuthHeaders, authenticatedFetch } from './utils/auth.js'
import { getAuthModalModeForVisitor, initializeVisitorStatus } from './utils/visitorStatus.js'
import { ensureAuthEntrypoint } from './utils/uiAnalytics.js'
import {
  clearNavigationFromUrl,
  applyRecipesPathRedirect,
  getInitialTabSelection,
  APP_NAVIGATE_PLAN_EVENT,
  guestSetupPopAction,
  hasNavigationDeepLink,
  isAuthRequiredTab,
  normalizeSavedTab,
  readRecipeParams,
  readSetupStepFromLocation,
  readTabFromLocation,
  setupHistoryMove,
  tabForSetupStep,
  writePlanUrl,
  writeRecipeUrl,
  writeSetupStepToUrl,
  writeTabToUrl,
} from './utils/appNavigation.js'
import { clearCookModeResume } from './utils/cookModeResume.js'
import { importRecipeFromUrl, isRecipeSiteAuthErrorMsg, parseRecipeSiteAuthDomain, formatRecipeReimportMessage } from './utils/recipeImport.js'
import { OPEN_PANTRY_RECEIPT_EVENT } from './utils/pantryCleanup.js'
import { useScrollDirection } from './hooks/useScrollDirection.js'
import { useScrollIdle } from './hooks/useScrollIdle.js'
import { dayKeyFromDow, todayKeyInPlanWeek } from './utils/mealPlanHelpers.js'
import { Button, Card, Input } from './design-system/primitives/index.js'
import PlannerPanel from './components/planner/PlannerPanel.jsx'
import PantryCleanupHost from './components/pantry/PantryCleanupHost.jsx'
import { resetPantryStarterToStaples } from './components/pantry/pantryHelpers.js'
import StateBannerDevPage from './components/StateBanner/StateBannerDevPage.jsx'
import RecipeDetailSkeleton from './components/recipe/RecipeDetailSkeleton.jsx'
import HomePage from './components/home/HomePage.jsx'
import GuestKitchenStrip from './components/guest/GuestKitchenStrip.jsx'
import KeepKitchenDialog from './components/guest/KeepKitchenDialog.jsx'
import KeepSavedCard from './components/guest/KeepSavedCard.jsx'
import DiscardKitchenConfirm from './components/auth/DiscardKitchenConfirm.jsx'
import { useGuestKitchen } from './contexts/GuestKitchenContext.jsx'
import {
  markOnboardingSaveOffered,
  discardExpiredGuestRecovery,
  mergeGuestKitchen,
  ONBOARDING_RECIPES_TO_CONTINUE,
  onboardingRecipeProgressLabel,
} from './utils/guestKitchen.js'
import {
  dismissKeepOk,
  readAccountSnapshot,
  readKeepResult,
  writeKeepResult,
} from './utils/accountSnapshot.js'
import { useToast } from './hooks/useToast.js'
import { GuestPlanSurface } from './components/guest/GuestWorkingSurfaces.jsx'
import OnboardingPlan from './components/onboarding/OnboardingPlan.jsx'
import { scrollMainContentToTop } from './utils/scrollElementToScrollRootTop.js'
import StepIndicator from './design-system/patterns/StepIndicator.jsx'
const RecipeDetail = lazy(() => import('./components/recipe/RecipeDetail.jsx'))
const CookMode = lazy(() => import('./components/recipe/CookMode.jsx'))

function RecipeScreenLoading({ label }) {
  return (
    <p className="recipe-screen-loading" aria-live="polite">
      <i className="fas fa-spinner fa-spin icon-sm" aria-hidden="true" /> {label}
    </p>
  )
}

function OnboardingStepFooter({ label, count = null, disabled = false, onClick, status = '' }) {
  const counted = Number.isFinite(count)
  const noun = count === 1 ? 'recipe' : 'recipes'
  return (
    <footer className="onboarding-step-footer">
      <Button
        variant="primary"
        onClick={onClick}
        disabled={disabled}
        aria-label={counted ? `${label}, ${count} ${noun}` : undefined}
      >
        {label}
        {counted ? (
          <span className="button-count" aria-hidden="true">{count}</span>
        ) : null}
      </Button>
      {status ? <p className="recipe-start__status">{status}</p> : null}
    </footer>
  )
}

/** Let the planned dinners paint on the setup week before the save prompt covers them. */
const ONBOARDING_SAVE_PROMPT_DELAY_MS = 800

function guestSetupLockReason(step) {
  if (step === 1) return 'Stock your pantry, then continue.'
  if (step === 2) return 'Add a few recipes, then continue.'
  if (step === 3) return 'Add your name, then continue.'
  return 'Plan the week, then continue.'
}

function GuestOnboardingProgress({
  step,
  onSelectStep,
}) {
  return (
    <section
      className="guest-workspace guest-workspace--tab-header"
      aria-label={`Kitchen setup step ${step} of 4`}
    >
      <StepIndicator
        steps={['Pantry', 'Recipes', 'Preferences', 'Plan']}
        activeIndex={step - 1}
        label="Kitchen setup progress"
        onStepSelect={onSelectStep}
      />
    </section>
  )
}
import { formatRecipeAskMessage } from './utils/recipeAskCaptainCook.js'

const MOBILE_LAYOUT_BREAKPOINT = 768

function getTodayCookTargetFromPlan(plan) {
  if (!plan?.week_start_date || !Array.isArray(plan.items)) return null
  const todayKey = todayKeyInPlanWeek(plan.week_start_date)
  if (!todayKey) return null

  const todayItem = plan.items.find((item) => (
    item
    && item.recipe_id
    && dayKeyFromDow(item.day_of_week) === todayKey
    && (item.status ?? 'planned') === 'planned'
  ))
  if (!todayItem) return null

  return {
    recipeId: String(todayItem.recipe_id),
    planItemId: String(todayItem.id),
    recipeName: todayItem.recipe_name || 'Tonight’s meal',
  }
}

function useTodayCookTarget({ enabled }) {
  const [target, setTarget] = useState(null)

  const refresh = useCallback(async () => {
    if (!enabled) {
      setTarget(null)
      return
    }
    try {
      const res = await authenticatedFetch('/meal-plans/current')
      if (!res.ok) {
        setTarget(null)
        return
      }
      const plan = await res.json().catch(() => null)
      setTarget(getTodayCookTargetFromPlan(plan))
    } catch {
      setTarget(null)
    }
  }, [enabled])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    if (!enabled) return undefined
    const onPlanUpdated = (event) => {
      const plan = event?.detail?.plan
      if (plan) {
        setTarget(getTodayCookTargetFromPlan(plan))
        return
      }
      void refresh()
    }
    window.addEventListener('mealPlanCurrentUpdated', onPlanUpdated)
    return () => {
      window.removeEventListener('mealPlanCurrentUpdated', onPlanUpdated)
    }
  }, [enabled, refresh])

  return { target, refresh }
}

/**
 * During streaming, markdown is often temporarily unbalanced (e.g. an opening ** without the closing **).
 * The UI used to switch between plain pre-wrap and ReactMarkdown when parity flipped, which caused a
 * strong flicker. Appending synthetic closers keeps parsing stable; they are removed once the real
 * closing markers arrive in the stream.
 */
function stableAssistantMarkdownSource(raw) {
  const s = String(raw ?? '')
  let out = s
  const doubleAsterisk = (out.match(/\*\*/g) || []).length
  if (doubleAsterisk % 2 === 1) out += '**'
  const doubleUnderscore = (out.match(/__/g) || []).length
  if (doubleUnderscore % 2 === 1) out += '__'
  return out
}

function UnifiedSearch({
  onSendMessage,
  loading,
  showRawResults,
  onFetchRawResults,
  onOpenHelp,
  onShortcutSelect,
  shortcutRefreshTrigger = 0,
  hasMessages = false,
  showChatShortcuts = true,
  onClearChat,
  panelEmbedded = false,
}) {
  const [input, setInput] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [recognitionError, setRecognitionError] = useState('')
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const recognitionRef = useRef(null)
  const finalTranscriptsRef = useRef('')
  const inputRef = useRef(input)
  const textareaRef = useRef(null)
  const headerSectionRef = useRef(null)
  const [headerHeight, setHeaderHeight] = useState(null)
  const [hasShortcuts, setHasShortcuts] = useState(true) // assume true until we know (avoids initial collapse)

  // Keep inputRef in sync with input state
  useEffect(() => {
    inputRef.current = input
  }, [input])

  // Auto-resize textarea to fit content (max 3 lines) - ChatGPT-like behavior
  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get accurate scrollHeight
      textareaRef.current.style.height = 'auto'
      const scrollHeight = textareaRef.current.scrollHeight
      const lineHeight = parseFloat(getComputedStyle(textareaRef.current).lineHeight) || 21
      const paddingTop = parseFloat(getComputedStyle(textareaRef.current).paddingTop) || 12
      const paddingBottom = parseFloat(getComputedStyle(textareaRef.current).paddingBottom) || 12
      const minHeight = lineHeight + paddingTop + paddingBottom // Single line
      const maxHeight = (lineHeight * 3) + paddingTop + paddingBottom // Max 3 lines
      
      // Calculate new height
      const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight))
      textareaRef.current.style.height = `${newHeight}px`
      
      // Show scrollbar only if content exceeds 3 lines
      textareaRef.current.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden'
    }
  }, [input])

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Auto-expand when user starts typing or when input changes
  useEffect(() => {
    if (input.trim() && isCollapsed && isMobile) {
      setIsCollapsed(false)
    }
  }, [input, isCollapsed, isMobile])

  // Measure and maintain header section height to prevent layout shift.
  // Use ResizeObserver so when chat suggestions load empty the reserved height collapses to hug the input.
  useEffect(() => {
    const el = headerSectionRef.current
    if (!el || !(!isMobile || !isCollapsed)) return

    const measureHeight = () => {
      if (headerSectionRef.current && !hasMessages) {
        const height = headerSectionRef.current.offsetHeight
        setHeaderHeight(height > 0 ? height : null)
      }
    }

    measureHeight()
    const timeout = setTimeout(measureHeight, 100)

    const resizeObserver = new ResizeObserver(() => {
      if (!hasMessages) measureHeight()
    })
    resizeObserver.observe(el)

    return () => {
      clearTimeout(timeout)
      resizeObserver.disconnect()
    }
  }, [hasMessages, isMobile, isCollapsed, shortcutRefreshTrigger])

  // Initialize speech recognition
  useEffect(() => {
    // Check for browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    
    if (!SpeechRecognition) {
      setRecognitionError('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.')
      return
    }

    // Create recognition instance
    const recognition = new SpeechRecognition()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'

    recognition.onstart = () => {
      setIsRecording(true)
      setRecognitionError('')
      // Preserve existing input when starting (use ref to get current value)
      finalTranscriptsRef.current = inputRef.current
    }

    recognition.onresult = (event) => {
      let interimTranscript = ''

      // Process all results since last resultIndex
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          finalTranscriptsRef.current += transcript + ' '
        } else {
          interimTranscript += transcript
        }
      }

      // Update input with accumulated final transcripts + current interim
      setInput(finalTranscriptsRef.current.trim() + (interimTranscript ? ' ' + interimTranscript : ''))
    }

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error)
      
      if (event.error === 'no-speech') {
        setRecognitionError('No speech detected. Try again.')
      } else if (event.error === 'audio-capture') {
        setRecognitionError('No microphone found. Please check your microphone.')
      } else if (event.error === 'not-allowed') {
        setRecognitionError('Microphone permission denied. Please allow microphone access.')
      } else if (event.error === 'network') {
        setRecognitionError('Network error. Please check your connection.')
      } else {
        setRecognitionError(`Recognition error: ${event.error}`)
      }
      
      setIsRecording(false)
    }

    recognition.onend = () => {
      setIsRecording(false)
    }

    recognitionRef.current = recognition

    // Cleanup on unmount
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop()
        } catch {
          // Ignore errors when stopping
        }
      }
    }
  }, [])


  // Toggle recording
  function toggleRecording() {
    if (!recognitionRef.current) {
      setRecognitionError('Speech recognition is not available.')
      return
    }

    if (isRecording) {
      try {
        recognitionRef.current.stop()
        setIsRecording(false)
      } catch (e) {
        console.error('Error stopping recognition:', e)
        setIsRecording(false)
      }
    } else {
      try {
        setRecognitionError('')
        recognitionRef.current.start()
      } catch (e) {
        console.error('Error starting recognition:', e)
        setRecognitionError('Failed to start recording. Please try again.')
      }
    }
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (input.trim()) {
        onSendMessage(input)
        // If raw results toggle is enabled, fetch raw results for this query
        if (showRawResults && onFetchRawResults) {
          onFetchRawResults(input)
        }
        setInput('')
        // Collapse on mobile after sending
        if (isMobile) {
          setIsCollapsed(true)
        }
      }
    }
  }

  function handleSendClick() {
    if (input.trim()) {
      onSendMessage(input)
      // If raw results toggle is enabled, fetch raw results for this query
      if (showRawResults && onFetchRawResults) {
        onFetchRawResults(input)
      }
      setInput('')
      // Collapse on mobile after sending
      if (isMobile) {
        setIsCollapsed(true)
      }
    }
  }

  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
  const isVoiceSupported = !!SpeechRecognition

  function handleShortcutSelect(phrase) {
    setInput(phrase)
    // Expand if collapsed on mobile
    if (isMobile && isCollapsed) {
      setIsCollapsed(false)
    }
    // Track which shortcut phrase was selected
    if (onShortcutSelect) {
      onShortcutSelect(phrase)
    }
    // Focus the textarea
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus()
        // Move cursor to end
        textareaRef.current.setSelectionRange(phrase.length, phrase.length)
      }
    }, 0)
  }

  const reserveComposerHeaderSpace =
    hasMessages && showChatShortcuts && hasShortcuts && headerHeight

  return (
    <div className={`composer ${isMobile && isCollapsed ? 'composer-collapsed' : ''}`}>
      {(!isMobile || !isCollapsed) && (
        <div
          ref={headerSectionRef}
          className={[
            'chat-composer-header-section',
            hasMessages ? 'chat-composer-header-section--hidden' : '',
            reserveComposerHeaderSpace ? 'chat-composer-header-section--reserve' : '',
          ].filter(Boolean).join(' ')}
          style={
            reserveComposerHeaderSpace
              ? { '--chat-composer-header-height': `${headerHeight}px` }
              : undefined
          }
        >
          {!panelEmbedded && (
            <div className="chat-composer-title-row">
              <div className="chat-composer-title">
                <i className="fas fa-comments icon-lg icon-primary" aria-hidden="true"></i> Chat
                {onOpenHelp && (
                  <button
                    onClick={onOpenHelp}
                    type="button"
                    aria-label="Help"
                    title="Help"
                    className="ghost chat-composer-help-btn"
                  >
                    <i className="fas fa-info-circle icon-base icon-info" aria-hidden="true"></i>
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Chat Shortcuts - set showChatShortcuts={false} in App to disable (e.g. to test if they affect chat history visibility) */}
          {showChatShortcuts && (
            <ChatShortcuts
              onSelect={handleShortcutSelect}
              mode="chat"
              refreshTrigger={shortcutRefreshTrigger}
              onShortcutsAvailable={setHasShortcuts}
            />
          )}
        </div>
      )}
      
      <div className="chat-composer-input-row">
        {hasMessages && onClearChat && !panelEmbedded && (
          <button
            type="button"
            className="chat-clear-btn"
            onClick={onClearChat}
            aria-label="Clear conversation"
            title="Clear conversation"
          >
            <i className="fas fa-trash-alt" aria-hidden="true" />
          </button>
        )}
        <div className="composer-pill composer-pill--ai">
          <span className="composer-pill__sparkle" aria-hidden="true">✦</span>
          <textarea
            id="chat-input"
            name="chat-message"
            ref={textareaRef}
            rows={1}
            className="composer-pill__input chat-input-expandable"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            onFocus={() => {
              if (isMobile && isCollapsed) {
                setIsCollapsed(false)
              }
            }}
            placeholder={
              isMobile
                ? 'Ask about recipes, pantry, or shopping...'
                : panelEmbedded
                  ? 'Ask about your recipes, pantry, or shopping list...'
                  : 'Ask me anything about your documents, recipes, or grocery items...'
            }
            aria-label={
              panelEmbedded
                ? 'Ask about your recipes, pantry, or shopping list'
                : 'Ask questions about your documents, recipes, or grocery items'
            }
          />
          {isVoiceSupported && (
            <button
              onClick={toggleRecording}
              className={`composer-pill__voice voice-button ${isRecording ? 'recording' : ''}`}
              aria-label={isRecording ? 'Stop recording' : 'Start voice input'}
              title={isRecording ? 'Stop recording (click again to stop)' : 'Start voice input'}
              type="button"
            >
              <i className={`fas ${isRecording ? 'fa-stop' : 'fa-microphone'} voice-button-icon`} aria-hidden="true"></i>
              {isRecording && <span className="recording-pulse" aria-hidden="true"></span>}
            </button>
          )}
          <button
            id="send-message-button"
            onClick={handleSendClick}
            disabled={loading || !input.trim()}
            className={`composer-pill__submit composer-pill__submit--ai chat-send-button ${loading ? 'loading' : ''}`}
            aria-label={loading ? 'Thinking' : 'Send message'}
            type="button"
          >
            {loading ? (
              <i className="fas fa-brain icon-base icon-inverse icon-pulse" aria-hidden="true"></i>
            ) : (
              <svg
                className="chat-send-icon"
                aria-hidden="true"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M3 8h8"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 4l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Voice recognition error */}
      {recognitionError && (
        <div className="error chat-voice-error">
          <span><i className="fas fa-exclamation-triangle icon-sm icon-warning" aria-hidden="true"></i> {recognitionError}</span>
          <button
            onClick={() => setRecognitionError('')}
            className="shop-inline-error-dismiss"
            aria-label="Close error message"
          >
            ×
          </button>
        </div>
      )}

      {isRecording && !recognitionError && (
        <div className="chat-listening-status">
          <i className="fas fa-circle icon-sm icon-inverse chat-listening-pulse" aria-hidden="true"></i> Listening...
        </div>
      )}
    </div>
  )
}

function loadSavedMessages() {
  try {
    const saved = JSON.parse(localStorage.getItem('agent_messages') || '[]')
    return Array.isArray(saved) ? saved : []
  } catch {
    return []
  }
}

export default function App() {
  if (
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('dev') === 'state-banners'
  ) {
    return <StateBannerDevPage />
  }
  return <AppMain />
}

function AppMain() {
  const [messages, setMessages] = useState(loadSavedMessages)
  const [loading, setLoading] = useState(false)
  const [_error, setError] = useState('')
  const [_theme, setTheme] = useState('light')
  const [abortController, setAbortController] = useState(null)
  const [helpModalOpen, setHelpModalOpen] = useState(false)
  const [showRawResults, _setShowRawResults] = useState(false) // Toggle for showing raw search results
  const [rawSearchResults, setRawSearchResults] = useState([]) // Raw search results from /rag/search
  const [rawSearchQuery, setRawSearchQuery] = useState('') // Last query used for raw search
  const initialTabSelectionRef = useRef(null)
  const hasUserSelectedTabRef = useRef(false)
  const [activeTab, setActiveTab] = useState(() => {
    const selection = getInitialTabSelection()
    initialTabSelectionRef.current = selection
    return selection.tab
  }) // 'shop', 'recipes', 'pantry', 'plan', 'account'
  const initialAccountNavigation = typeof window !== 'undefined' ? readInitialAccountNavigation() : null
  const [accountSubTab, setAccountSubTab] = useState(() => {
    if (initialAccountNavigation?.accountSubTab) return initialAccountNavigation.accountSubTab
    return 'preferences'
  }) // 'profile' | 'preferences' | 'security' | 'item-admin' | 'system-admin'
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false)
  const [askLauncherOpen, setAskLauncherOpen] = useState(false)
  const [lockedAskHintOpen, setLockedAskHintOpen] = useState(false)
  const askNavAnchorRef = useRef(null)
  const lockedAskAnchorRef = useRef(null)
  const pendingChatMessageRef = useRef(null) // When set, send this message after drawer opens (e.g. from Recipes featured prompts); ref avoids effect re-run cancelling the timeout
  /** Bumped on each Recipes featured/kid prompt click so pending send runs even when the drawer was already open (chatDrawerOpen alone would not retrigger the effect). */
  const [featuredPromptSendNonce, setFeaturedPromptSendNonce] = useState(0)
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_LAYOUT_BREAKPOINT
  )
  // Queue for add-to-list from Recipes: process one at a time to avoid parallel updates and browser refresh failures
  const loadListQueueRef = useRef([]) // { items, id, name, isRecipe? }[]
  const [currentLoadList, setCurrentLoadList] = useState(null) // currently applied list (one at a time)
  const [loadListQueueLength, setLoadListQueueLength] = useState(0) // remaining in queue (for progress UI)
  const [pendingRecipeImageFile, setPendingRecipeImageFile] = useState(null) // File when user selects/takes photo in Add Recipe → we switch to Shop and run upload
  const [showGoShoppingBanner, setShowGoShoppingBanner] = useState(false)
  const [pantryOpenReceipt, setPantryOpenReceipt] = useState(false)
  const [systemSubTab, setSystemSubTab] = useState(() => {
    if (initialAccountNavigation?.systemSubTab) return initialAccountNavigation.systemSubTab
    try {
      const saved = localStorage.getItem('agent_system_subtab')
      if (saved === 'analytics' || saved === 'config' || saved === 'documents' || saved === 'prompts') {
        return saved
      }
    } catch {
      // ignore
    }
    return 'analytics'
  }) // 'analytics', 'prompts', 'config', or 'documents'
  const [currentShortcutPhrase, setCurrentShortcutPhrase] = useState(null) // Track which shortcut was used
  const [shortcutRefreshTrigger, setShortcutRefreshTrigger] = useState(0) // Trigger to refresh shortcuts
  const endRef = useRef(null)
  const mainContentRef = useRef(null)
  const typeRef = useRef({ timer: null })
  const [lastAssistantUrl, setLastAssistantUrl] = useState('')
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [authModalMode, setAuthModalMode] = useState('login') // 'login' | 'register'
  const [isNewVisitor, setIsNewVisitor] = useState(false)
  const [showClearChatConfirm, setShowClearChatConfirm] = useState(false)
  const [showPrivacyView, setShowPrivacyView] = useState(false)
  const [currentSessionId, setCurrentSessionId] = useState(null) // Track session ID for clarification responses
  const [feedbackDraft, setFeedbackDraft] = useState({}) // { [messageIndex]: { rating, comment, taskCompleted } } for Phase 1 feedback
  const [feedbackSubmittingIndex, setFeedbackSubmittingIndex] = useState(null) // message index currently submitting
  const [feedbackSubmitError, setFeedbackSubmitError] = useState(null) // error message to show inline
  const [_headerVisible, setHeaderVisible] = useState(true)
  const messagesContainerRef = useRef(null)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const { isAuthenticated, isLoading: authLoading, checkAuth, logout } = useAuth()
  const {
    cohort,
    kitchen: guestKitchen,
    start: startGuest,
    startStatus,
    saveStatus: guestSaveStatus,
    update: updateGuest,
    retrySave: retryGuestSave,
    finishGuest,
    discardGuest,
    remoteSummary: guestSummary,
    shouldAutoShowKeep,
  } = useGuestKitchen()
  const { showToast } = useToast()
  const [keepOpen, setKeepOpen] = useState(false)
  const [guestHomeOpen, setGuestHomeOpen] = useState(false)
  const [discardOpen, setDiscardOpen] = useState(false)
  const [keepResult, setKeepResult] = useState(() => readKeepResult())
  const [keepResultStatus, setKeepResultStatus] = useState('success')
  const [mergeStatus, setMergeStatus] = useState('idle')
  const preKeepTabRef = useRef('pantry')
  const keepPromptTimerRef = useRef(null)
  const keepPromptArmedRef = useRef(false)
  const isWorkingGuest = !isAuthenticated && cohort === 'guest'
  const guestAttachPending = Boolean(guestKitchen) && isAuthenticated
  const showLoggedOutHome = !authLoading && !isAuthenticated && (!isWorkingGuest || guestHomeOpen)
  const guestOnboardingStep = Math.min(4, Math.max(1, Number(guestKitchen?.onboardingStep) || 1))
  const onboardingRecipeCount = guestSummary?.recipeCount || guestKitchen?.recipes?.length || 0
  const guestOnboardingActive = isWorkingGuest && guestKitchen?.onboardingComplete !== true
  const setupFocusActive = guestOnboardingActive
  const returningLoginFocused = showLoggedOutHome && cohort === 'returning'
  const navigationLocked = showLoggedOutHome || setupFocusActive
  const { selectedStore } = useSelectedStore()
  const { target: todayCookTarget } = useTodayCookTarget({
    enabled: isAuthenticated && !authLoading,
  })
  const todayCookShortcutTarget = useMemo(() => {
    if (!todayCookTarget?.recipeId || activeTab === 'cook') return null
    return todayCookTarget
  }, [todayCookTarget, activeTab])

  const refreshGoShoppingBanner = useCallback(async () => {
    if (!isAuthenticated) {
      setShowGoShoppingBanner(false)
      return
    }
    try {
      const res = await authenticatedFetch('/recipes/pantry-shopping-banner')
      if (!res.ok) return
      const data = await res.json()
      setShowGoShoppingBanner(Boolean(data?.show_go_shopping_banner))
    } catch {
      // keep previous value
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) {
      setShowGoShoppingBanner(false)
    }
  }, [isAuthenticated])

  const handlePantryInventorySettled = useCallback(() => {
    // Pantry's list request finishes first. The banner scores recipes only in the
    // empty-perishable case, and it must not compete with that first inventory read.
    refreshGoShoppingBanner()
  }, [refreshGoShoppingBanner])

  useEffect(() => {
    if (!isAuthenticated || authLoading) return
    if (activeTab !== 'recipes') return
    refreshGoShoppingBanner()
  }, [isAuthenticated, authLoading, activeTab, refreshGoShoppingBanner])

  useEffect(() => {
    const onOpenPantryReceipt = () => {
      hasUserSelectedTabRef.current = true
      setChatDrawerOpen(false)
      setAskLauncherOpen(false)
      setActiveTab('pantry')
      writeTabToUrl('pantry')
      setPantryOpenReceipt(true)
    }
    window.addEventListener(OPEN_PANTRY_RECEIPT_EVENT, onOpenPantryReceipt)
    return () => window.removeEventListener(OPEN_PANTRY_RECEIPT_EVENT, onOpenPantryReceipt)
  }, [])

  useEffect(() => {
    function onPantryInventoryChanged() {
      refreshGoShoppingBanner()
    }
    window.addEventListener('pantry-inventory-changed', onPantryInventoryChanged)
    return () => window.removeEventListener('pantry-inventory-changed', onPantryInventoryChanged)
  }, [refreshGoShoppingBanner])

  function extractShopListIdAndCleanText(text) {
    const raw = String(text || '')
    const match = raw.match(/\[SHOP_LIST_ID:([0-9a-fA-F-]{36})\]/)
    const shopListId = match?.[1] || null
    // Collapse horizontal whitespace only — \s{2,} would merge newlines and break markdown after stream `done`.
    const cleanedText = raw
      .replace(/\s*\[SHOP_LIST_ID:[0-9a-fA-F-]{36}\]\s*/g, ' ')
      .replace(/[^\S\n\r]+/g, ' ')
      .trim()
    return { shopListId, cleanedText }
  }

  // Basic new-vs-returning visitor detection without cookies.
  useEffect(() => {
    setIsNewVisitor(initializeVisitorStatus())
  }, [])

  const openAuthModal = useCallback(() => {
    ensureAuthEntrypoint('ui')
    setAuthModalMode(getAuthModalModeForVisitor(isNewVisitor))
    setShowLoginModal(true)
  }, [isNewVisitor])

  const openLoginModal = useCallback(() => {
    if (cohort === 'returning') {
      const emailField = document.getElementById('returning-login-email')
      if (emailField) {
        emailField.focus()
        return
      }
    }
    ensureAuthEntrypoint('login')
    setAuthModalMode('login')
    setShowLoginModal(true)
  }, [cohort])

  const guestAuthGateActive = Boolean(!isAuthenticated && showLoginModal)

  const [_hasPreferences, setHasPreferences] = useState(false)
  const prefsSummaryInFlight = useRef(false)
  
  // If auth completes successfully while the login modal is open (common on hard refresh),
  // close the modal so users aren't forced to interact with it.
  useEffect(() => {
    if (isAuthenticated && showLoginModal) {
      setShowLoginModal(false)
    }
  }, [isAuthenticated, showLoginModal])

  const handleLoginModalClose = useCallback(() => {
    setShowLoginModal(false)
  }, [])

  const handleOpenPrivacyFromAuth = useCallback(() => {
    setShowLoginModal(false)
    setShowPrivacyView(true)
  }, [])

  const openGuestHome = useCallback(() => {
    if (!isWorkingGuest) return
    setShowPrivacyView(false)
    setHelpModalOpen(false)
    setKeepOpen(false)
    setAskLauncherOpen(false)
    setLockedAskHintOpen(false)
    setGuestHomeOpen(true)
    requestAnimationFrame(() => {
      requestAnimationFrame(scrollMainContentToTop)
    })
  }, [isWorkingGuest])

  const handleStartGuest = useCallback(async () => {
    if (guestKitchen) {
      setGuestHomeOpen(false)
      hasUserSelectedTabRef.current = true
      const step = Math.min(4, Math.max(1, Number(guestKitchen.onboardingStep) || 1))
      setActiveTab(tabForSetupStep(step))
      writeSetupStepToUrl(step, { replace: true })
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollMainContentToTop)
      })
      return
    }
    try {
      await startGuest()
      hasUserSelectedTabRef.current = true
      setActiveTab('pantry')
      writeSetupStepToUrl(1, { replace: true })
    } catch {
      // HomePage renders the start error from context state.
    }
  }, [guestKitchen, startGuest])

  const applyGuestSetupStep = useCallback((nextStep, { history = 'push' } = {}) => {
    const step = Math.min(4, Math.max(1, Number(nextStep) || 1))
    if (history !== 'none' && step === 1) resetPantryStarterToStaples()
    updateGuest((current) => (
      current.onboardingStep === step && current.onboardingComplete === false
        ? current
        : { ...current, onboardingStep: step, onboardingComplete: false }
    ))
    setGuestHomeOpen(false)
    setActiveTab(tabForSetupStep(step))
    if (history === 'none') return
    writeSetupStepToUrl(step, { replace: history === 'replace' })
    requestAnimationFrame(() => {
      requestAnimationFrame(scrollMainContentToTop)
    })
  }, [updateGuest])

  const advanceGuestOnboarding = useCallback((nextStep) => {
    const step = Math.min(4, Math.max(1, Number(nextStep) || 1))
    const move = setupHistoryMove(guestOnboardingStep, step, window.history.state)
    if (move === 'none') return
    if (move === 'back') {
      window.history.back()
      return
    }
    applyGuestSetupStep(step, { history: move })
  }, [applyGuestSetupStep, guestOnboardingStep])

  useEffect(() => {
    if (!isWorkingGuest) return
    const urlStep = readSetupStepFromLocation(window.location)
    if (!urlStep) return
    if (guestKitchen?.onboardingComplete) {
      setGuestHomeOpen(false)
      setActiveTab('plan')
      writeTabToUrl('plan', { replace: true })
      return
    }
    if (!guestOnboardingActive) return
    if (urlStep === guestOnboardingStep && activeTab === tabForSetupStep(urlStep)) return
    applyGuestSetupStep(urlStep, { history: 'none' })
  }, [
    activeTab,
    applyGuestSetupStep,
    guestKitchen?.onboardingComplete,
    guestOnboardingActive,
    guestOnboardingStep,
    isWorkingGuest,
  ])

  useEffect(() => {
    if (!guestOnboardingActive || guestHomeOpen || guestOnboardingStep >= 4) return
    const urlStep = readSetupStepFromLocation(window.location)
    if (urlStep && urlStep !== guestOnboardingStep) return
    const allowedTabs = (
      guestOnboardingStep === 1
        ? ['pantry']
        : guestOnboardingStep === 2
          ? ['recipes', 'recipe', 'cook']
          : ['plan']
    )
    if (allowedTabs.includes(activeTab)) return
    setActiveTab(tabForSetupStep(guestOnboardingStep))
    if (urlStep !== guestOnboardingStep) {
      writeSetupStepToUrl(guestOnboardingStep, { replace: true })
    }
  }, [activeTab, guestHomeOpen, guestOnboardingActive, guestOnboardingStep])

  useLayoutEffect(() => {
    if (!guestOnboardingActive || guestOnboardingStep !== 3) return
    scrollMainContentToTop()
  }, [guestOnboardingActive, guestOnboardingStep])

  const openKeepDialog = useCallback(() => {
    preKeepTabRef.current = activeTab
    setKeepOpen(true)
  }, [activeTab])

  const promptOnboardingSave = useCallback(() => {
    if (!guestOnboardingActive || guestOnboardingStep !== 4 || keepOpen) return
    if (guestKitchen?.keepShown || guestKitchen?.keepDismissed || keepPromptArmedRef.current) return
    keepPromptArmedRef.current = true
    try {
      updateGuest(markOnboardingSaveOffered)
    } catch {
      keepPromptArmedRef.current = false
      return
    }
    if (keepPromptTimerRef.current) window.clearTimeout(keepPromptTimerRef.current)
    keepPromptTimerRef.current = window.setTimeout(() => {
      keepPromptTimerRef.current = null
      openKeepDialog()
    }, ONBOARDING_SAVE_PROMPT_DELAY_MS)
  }, [guestKitchen?.keepDismissed, guestKitchen?.keepShown, guestOnboardingActive, guestOnboardingStep, keepOpen, openKeepDialog, updateGuest])

  useEffect(() => () => {
    if (keepPromptTimerRef.current) window.clearTimeout(keepPromptTimerRef.current)
  }, [])

  const markOnboardingPlanInitialized = useCallback(() => {
    updateGuest((current) => (
      current.onboardingPlanInitialized
        ? current
        : { ...current, onboardingPlanInitialized: true }
    ))
  }, [updateGuest])

  const closeLockedAskHint = useCallback(() => {
    setLockedAskHintOpen(false)
  }, [])

  const promptLockedAsk = useCallback((anchorEl) => {
    lockedAskAnchorRef.current = anchorEl || askNavAnchorRef.current
    setLockedAskHintOpen(true)
  }, [])

  useEffect(() => {
    if (!isWorkingGuest || !shouldAutoShowKeep || keepOpen) return
    preKeepTabRef.current = activeTab
    try {
      updateGuest((current) => ({ ...current, keepShown: true }))
    } catch {
      return
    }
    setKeepOpen(true)
  }, [activeTab, isWorkingGuest, keepOpen, shouldAutoShowKeep, updateGuest])

  const handleKeepNotNow = useCallback(() => {
    if (keepPromptTimerRef.current) {
      window.clearTimeout(keepPromptTimerRef.current)
      keepPromptTimerRef.current = null
    }
    if (guestKitchen) {
      updateGuest((current) => ({
        ...current,
        keepShown: true,
        keepDismissed: true,
      }))
    }
    setKeepOpen(false)
  }, [guestKitchen, updateGuest])

  const attachGuestKitchen = useCallback(async () => {
    setMergeStatus('busy')
    try {
      const result = await mergeGuestKitchen()
      writeKeepResult(result)
      setKeepResult(result)
      setKeepResultStatus('success')
      finishGuest()
      setKeepOpen(false)
      setMergeStatus('idle')
      setActiveTab('plan')
      writeTabToUrl('plan', { replace: true })
      return result
    } catch {
      setKeepOpen(false)
      setMergeStatus('error')
      setKeepResultStatus('failure')
      setKeepResult({ mergeId: 'pending' })
      return null
    }
  }, [finishGuest])

  const handleKeepLoginInstead = useCallback(() => {
    setKeepOpen(false)
    openLoginModal()
  }, [openLoginModal])

  const handleExistingAccountLogin = useCallback(() => {
    setShowLoginModal(false)
    setKeepOpen(false)
    setDiscardOpen(true)
  }, [])

  const finishAuthLogin = useCallback(() => {
    setShowLoginModal(false)
    if (guestKitchen) handleExistingAccountLogin()
    else {
      if (cohort === 'expired') discardExpiredGuestRecovery()
      checkAuth()
    }
  }, [guestKitchen, handleExistingAccountLogin, cohort, checkAuth])

  const handleDiscardConfirm = useCallback(() => {
    discardGuest()
    setDiscardOpen(false)
    showToast('Kitchen discarded. You’re logged in.')
  }, [discardGuest, showToast])

  const handleDiscardCancel = useCallback(() => {
    setDiscardOpen(false)
    logout()
  }, [logout])

  const handleKeepDismiss = useCallback(() => {
    if (keepResult?.mergeId && keepResult.mergeId !== 'pending') dismissKeepOk(keepResult.mergeId)
    setKeepResult(null)
  }, [keepResult])

  // Set to false to disable chat shortcuts (e.g. to test if they affect chat history visibility)
  const enableChatShortcuts = true
  // Dev-only: disable chat rehydration/scroll to pinpoint "data appears then disappears" (see query params below)
  const devParams = typeof window !== 'undefined' && import.meta.env.DEV ? new URLSearchParams(window.location.search) : null
  const disableChatRehydration = devParams?.get('disableChatRehydration') === '1' || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('disableChatRehydration') === '1')
  const disableChatScroll = devParams?.get('disableChatScroll') === '1' || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('disableChatScroll') === '1')
  const devLogOnceRef = useRef({ rehydration: false, scroll: false })
  // Usage: ?disableChatRehydration=1 or ?disableChatScroll=1 (or set sessionStorage key to '1'); remove to re-enable.
  
  /* Address-bar minimization disabled to match production scroll behavior (single scroll container, no body scroll) */
  useScrollDirection({ enabled: false, threshold: 5 })
  const isMainContentScrolling = useScrollIdle(mainContentRef, {
    enabled: isMobileViewport && activeTab !== 'cook',
    idleMs: 180,
  })

  const refreshPreferencesSummary = useCallback(async () => {
    if (!isAuthenticated) {
      setHasPreferences((prev) => (prev === false ? prev : false))
      return
    }
    if (prefsSummaryInFlight.current) return
    prefsSummaryInFlight.current = true
    try {
      const res = await fetch('/preferences/me/summary', { headers: { ...getAuthHeaders() } })
      const responseText = await res.text()
      let data = {}
      try {
        data = responseText.trim() ? JSON.parse(responseText) : {}
      } catch (parseErr) {
        console.error('refreshPreferencesSummary: JSON parse error', parseErr)
        data = {}
      }
      if (!res.ok) {
        setHasPreferences((prev) => (prev === false ? prev : false))
        return
      }
      const hasPrefs = Boolean(data.has_preferences)
      setHasPreferences((prev) => (prev === hasPrefs ? prev : hasPrefs))
    } catch (error) {
      console.error('refreshPreferencesSummary: Exception caught', error)
      setHasPreferences((prev) => (prev === false ? prev : false))
    } finally {
      prefsSummaryInFlight.current = false
    }
  }, [isAuthenticated])

  const handlePreferencesChanged = useCallback(() => {
    void refreshPreferencesSummary()
  }, [refreshPreferencesSummary])

  // When signed out, clear the summary flag. When signed in, refresh once the auth UI is dismissed
  // (avoids racing /preferences/me/summary with a half-open login flow). Also re-run when auth flips.
  useEffect(() => {
    if (!isAuthenticated) {
      setHasPreferences((prev) => (prev === false ? prev : false))
      return
    }
    if (showLoginModal) return
    void checkAuth()
    void refreshPreferencesSummary()
  }, [isAuthenticated, showLoginModal, checkAuth, refreshPreferencesSummary])

  // Home cohorts stay out of working tabs. A live guest kitchen uses the real tab shell.
  useEffect(() => {
    if (authLoading || isAuthenticated || isWorkingGuest) return
    if (activeTab === 'recipe' || activeTab === 'cook') return
    if (activeTab !== 'plan') {
      setActiveTab('plan')
    }
    clearNavigationFromUrl()
  }, [authLoading, isAuthenticated, isWorkingGuest, activeTab])

  // Guard: auth-only tabs redirect to Plan without forcing the auth modal.
  useEffect(() => {
    if (authLoading) return
    if (isAuthenticated || isWorkingGuest) return
    if (activeTab === 'recipe' || activeTab === 'cook') return
    if (isAuthRequiredTab(activeTab)) {
      setActiveTab('plan')
      clearNavigationFromUrl()
    }
  }, [activeTab, isAuthenticated, isWorkingGuest, authLoading])

  const goToPlanHome = useCallback(() => {
    if (hasNavigationDeepLink(window.location)) return
    setActiveTab('plan')
    writeTabToUrl('plan', { replace: true })
  }, [])

  const goToPantryHome = useCallback(() => {
    if (hasNavigationDeepLink(window.location)) return
    setActiveTab('pantry')
    writeTabToUrl('pantry', { replace: true })
  }, [])

  // Authenticated first load defaults to Plan unless the user/deep link picked another tab.
  useEffect(() => {
    if (authLoading || !isAuthenticated) return
    if (initialTabSelectionRef.current?.source !== 'default') return
    if (hasUserSelectedTabRef.current) return
    if (activeTab === 'plan') return

    goToPlanHome()
  }, [activeTab, isAuthenticated, authLoading, goToPlanHome])

  // After sign-in, route new registrations to Pantry starter; returning users to Plan.
  useEffect(() => {
    function handleUserLoggedIn() {
      try {
        if (sessionStorage.getItem('agent_post_auth_route_pantry') === '1') {
          sessionStorage.removeItem('agent_post_auth_route_pantry')
          goToPantryHome()
          return
        }
      } catch {
        // ignore
      }
      goToPlanHome()
    }
    window.addEventListener('userLoggedIn', handleUserLoggedIn)
    return () => window.removeEventListener('userLoggedIn', handleUserLoggedIn)
  }, [goToPlanHome, goToPantryHome])

  // Logout: drop remembered tab/URL so the next sign-in lands on Plan (unless URL deep link).
  useEffect(() => {
    function handleUserLoggedOut() {
      localStorage.removeItem('agent_active_tab')
      clearCookModeResume()
      clearNavigationFromUrl()
      initialTabSelectionRef.current = { tab: 'plan', source: 'default' }
      hasUserSelectedTabRef.current = false
      setActiveTab('plan')
    }
    window.addEventListener('userLoggedOut', handleUserLoggedOut)
    return () => window.removeEventListener('userLoggedOut', handleUserLoggedOut)
  }, [])

  // Logged out: never leave the chat drawer open (e.g. after logout while drawer was open).
  useEffect(() => {
    if (authLoading) return
    if (!isAuthenticated && chatDrawerOpen) {
      setChatDrawerOpen(false)
    }
    if (!isAuthenticated && askLauncherOpen) {
      setAskLauncherOpen(false)
    }
    if (isAuthenticated && lockedAskHintOpen) {
      setLockedAskHintOpen(false)
    }
  }, [authLoading, isAuthenticated, chatDrawerOpen, askLauncherOpen, lockedAskHintOpen])

  // Scroll/layout: single scroll container (.main-content); no body scroll. See docs/SCROLL_AND_LAYOUT_APPROACH.md

  // Load theme and migrate legacy tab values in localStorage (active tab now comes from URL/localStorage init).
  useEffect(() => {
    const savedTheme = localStorage.getItem('agent_theme') || 'light'
    setTheme(savedTheme)
    document.documentElement.setAttribute('data-theme', savedTheme)
    
    // Handle migration from old admin structure and old tab names (grocery→shop, chat→recipes)
    const rawSavedTab = localStorage.getItem('agent_active_tab') || 'plan'
    const savedTab = normalizeSavedTab(rawSavedTab)
    const savedAdminSubTab = localStorage.getItem('agent_admin_subtab')
    
    if (rawSavedTab === 'admin' || rawSavedTab === 'documents') {
      // Migrate old admin tab structure → all admin sections now live inside Account
      if (savedAdminSubTab === 'documents') {
        setAccountSubTab('system-admin')
        setSystemSubTab('documents')
        localStorage.setItem('agent_active_tab', 'account')
        localStorage.setItem('agent_system_subtab', 'documents')
      } else if (savedAdminSubTab === 'categories' || savedAdminSubTab === 'synonyms') {
        setAccountSubTab('item-admin')
        localStorage.setItem('agent_active_tab', 'account')
      } else {
        setAccountSubTab('system-admin')
        setSystemSubTab(savedAdminSubTab || 'analytics')
        localStorage.setItem('agent_active_tab', 'account')
        localStorage.setItem('agent_system_subtab', savedAdminSubTab || 'analytics')
      }
      localStorage.removeItem('agent_admin_subtab')
      localStorage.removeItem('agent_item_subtab')
    } else if (rawSavedTab === 'item-admin') {
      // item-admin was a top-level tab; now it's inside Account
      setAccountSubTab('item-admin')
      localStorage.setItem('agent_active_tab', 'account')
    } else if (rawSavedTab === 'system-admin') {
      // system-admin was a top-level tab; now it's inside Account
      setAccountSubTab('system-admin')
      localStorage.setItem('agent_active_tab', 'account')
    } else {
      // Standard tab: persist normalized value if legacy alias was found
      if (rawSavedTab !== savedTab) {
        localStorage.setItem('agent_active_tab', savedTab)
      }
    }
    
    // Load sub-tabs for new structure (if not migrated above)
    if (rawSavedTab !== 'admin' && rawSavedTab !== 'documents') {
      const savedSystemSubTab = localStorage.getItem('agent_system_subtab')
      if (savedSystemSubTab && (savedSystemSubTab === 'analytics' || savedSystemSubTab === 'config' || savedSystemSubTab === 'documents')) {
        setSystemSubTab(savedSystemSubTab)
      } else if (savedSystemSubTab === 'cart-migration') {
        setSystemSubTab('analytics')
        localStorage.setItem('agent_system_subtab', 'analytics')
      }
    }

    // Chat history is initialized via useState(loadSavedMessages) so first paint has
    // restored history and we avoid "appear then disappear" in production.
  }, [])

  // Persist history (only when non-empty so dev Strict Mode never overwrites saved history with [])
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('agent_messages', JSON.stringify(messages))
    }
    // When empty we don't write - clearChat() already calls removeItem
  }, [messages])

  // Dev-only: detect when messages go from non-empty to empty (pinpoint "data appears then disappears")
  const prevMessagesLengthRef = useRef(messages.length)
  if (import.meta.env.DEV && prevMessagesLengthRef.current > 0 && messages.length === 0) {
    console.warn('[dev] Chat messages just became EMPTY (were', prevMessagesLengthRef.current, '). Check above for clearChat() stack or another setMessages([]) caller.')
  }
  prevMessagesLengthRef.current = messages.length

  // Rehydrate chat history from localStorage when chat drawer is open and state is empty.
  useEffect(() => {
    if (disableChatRehydration) {
      if (messages.length === 0 && chatDrawerOpen && !devLogOnceRef.current.rehydration) {
        devLogOnceRef.current.rehydration = true
        console.log('[dev] Chat rehydration DISABLED (messages would be loaded from localStorage). Use ?disableChatRehydration=0 or remove sessionStorage to re-enable.')
      }
      return
    }
    if (!chatDrawerOpen) return
    const saved = loadSavedMessages()
    if (saved.length === 0) return
    setMessages(prev => (prev.length > 0 ? prev : saved))
  }, [chatDrawerOpen, messages.length, disableChatRehydration])

  // Persist active tab while signed in (cleared on logout so the next sign-in defaults to Plan).
  // Overlay screens (recipe/cook) persist as their parent Recipes tab; cook resume uses cook_mode_resume_v1.
  useEffect(() => {
    if (!isAuthenticated) return
    const tabToStore = (activeTab === 'cook' || activeTab === 'recipe') ? 'recipes' : activeTab
    localStorage.setItem('agent_active_tab', tabToStore)
  }, [activeTab, isAuthenticated])

  // Drop cold-start resume only when leaving Cook Mode (not on unrelated cold starts).
  const prevActiveTabRef = useRef(activeTab)
  useEffect(() => {
    const prev = prevActiveTabRef.current
    prevActiveTabRef.current = activeTab
    if (prev === 'cook' && activeTab !== 'cook') {
      clearCookModeResume()
    }
  }, [activeTab])

  // Keep URL in sync while signed in (reset nav URL when logged out so sign-in can default to Plan).
  // Exception: recipe/cook screens keep their URL even when unauthenticated (they're read-accessible).
  useEffect(() => {
    const isRecipeScreen = activeTab === 'recipe' || activeTab === 'cook'
    if (!isAuthenticated && !isWorkingGuest && !isRecipeScreen) return
    if (isWorkingGuest && readSetupStepFromLocation(window.location)) return
    const currentUrlTab = readTabFromLocation(window.location)
    if (currentUrlTab !== activeTab) {
      if (activeTab === 'recipe' || activeTab === 'cook') return
      writeTabToUrl(activeTab, { replace: true })
    }
  }, [activeTab, isAuthenticated, isWorkingGuest])

  // Back/forward should restore page destination from path + search.
  useEffect(() => {
    function handlePopState() {
      applyRecipesPathRedirect()
      const setupStep = readSetupStepFromLocation(window.location)
      const setupAction = guestSetupPopAction({
        setupStep,
        onboardingComplete: guestKitchen?.onboardingComplete === true,
        onboardingActive: guestOnboardingActive,
      })
      if (setupAction === 'plan') {
        setGuestHomeOpen(false)
        setActiveTab('plan')
        writeTabToUrl('plan', { replace: true })
        return
      }
      if (setupAction === 'sync-step') {
        applyGuestSetupStep(setupStep, { history: 'none' })
        setRawSearchResults([])
        setRawSearchQuery('')
        return
      }
      if (setupAction === 'home') {
        setGuestHomeOpen(true)
        setRawSearchResults([])
        setRawSearchQuery('')
        return
      }
      const targetTab = readTabFromLocation(window.location) || 'plan'
      if (isAuthRequiredTab(targetTab) && !isAuthenticated && !isWorkingGuest) {
        setActiveTab('plan')
        clearNavigationFromUrl()
        return
      }
      setActiveTab(targetTab)
      setRawSearchResults([])
      setRawSearchQuery('')
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [
    applyGuestSetupStep,
    guestKitchen?.onboardingComplete,
    guestOnboardingActive,
    isAuthenticated,
    isWorkingGuest,
  ])

  // Recipe detail links in chat: navigate to the recipe page and close the drawer.
  useEffect(() => {
    function onOpenRecipeFromChat() {
      hasUserSelectedTabRef.current = true
      setChatDrawerOpen(false)
      setAskLauncherOpen(false)
    }
    window.addEventListener('agent:open-recipe-from-chat', onOpenRecipeFromChat)
    return () => window.removeEventListener('agent:open-recipe-from-chat', onOpenRecipeFromChat)
  }, [])


  // Persist system admin sub-tab (system-admin is now a sub-tab inside Account)
  useEffect(() => {
    if (accountSubTab === 'system-admin') {
      localStorage.setItem('agent_system_subtab', systemSubTab)
    }
  }, [systemSubTab, accountSubTab])

  // Deep link: Analytics → Settings → Workflows (e.g. /account#pantry-match-review)
  useEffect(() => {
    function onNavigateSettings(event) {
      const hash = event?.detail?.hash || readSettingsDeepLinkHash()
      if (!hash) return
      setActiveTab('account')
      setAccountSubTab('system-admin')
      setSystemSubTab('config')
      writeTabToUrl('account', { replace: true })
      window.location.hash = hash
    }

    window.addEventListener(APP_NAVIGATE_SETTINGS_EVENT, onNavigateSettings)
    return () => window.removeEventListener(APP_NAVIGATE_SETTINGS_EVENT, onNavigateSettings)
  }, [])

  // Cook → Plan handoff: optional highlight of a pinned pick / scheduled recipe
  useEffect(() => {
    function onNavigatePlan(event) {
      const highlight = event?.detail?.highlight ?? null
      const weekStart = event?.detail?.weekStart ?? null
      hasUserSelectedTabRef.current = true
      setChatDrawerOpen(false)
      setAskLauncherOpen(false)
      if (!isAuthenticated && !isWorkingGuest) {
        openAuthModal()
        return
      }
      writePlanUrl({ highlight, weekStart }, { replace: false })
      setActiveTab('plan')
      setRawSearchResults([])
      setRawSearchQuery('')
    }
    window.addEventListener(APP_NAVIGATE_PLAN_EVENT, onNavigatePlan)
    return () => window.removeEventListener(APP_NAVIGATE_PLAN_EVENT, onNavigatePlan)
  }, [isAuthenticated, isWorkingGuest, openAuthModal])

  function handleTabChange(tab, subTab) {
    hasUserSelectedTabRef.current = true
    // Main nav should dismiss the chat overlay and launcher so the selected screen is visible
    setChatDrawerOpen(false)
    setAskLauncherOpen(false)
    // Support routing to account sub-tabs: handleTabChange('account', 'preferences')
    if (tab === 'account' && subTab) {
      setAccountSubTab(subTab)
    }
    // Migrate legacy ghost tab names to account
    if (tab === 'profile') {
      setAccountSubTab('profile')
      if (!isAuthenticated) {
        openAuthModal()
        return
      }
      setActiveTab('account')
      if (activeTab !== 'account') writeTabToUrl('account')
      setRawSearchResults([])
      setRawSearchQuery('')
      return
    }
    if (tab === 'preferences') {
      setAccountSubTab('preferences')
      if (!isAuthenticated) {
        openAuthModal()
        return
      }
      setActiveTab('account')
      if (activeTab !== 'account') writeTabToUrl('account')
      setRawSearchResults([])
      setRawSearchQuery('')
      return
    }
    // Admin sections live inside Account tab
    if (tab === 'item-admin') {
      setAccountSubTab('item-admin')
      if (!isAuthenticated) {
        openAuthModal()
        return
      }
      setActiveTab('account')
      if (activeTab !== 'account') writeTabToUrl('account')
      setRawSearchResults([])
      setRawSearchQuery('')
      return
    }
    if (tab === 'system-admin') {
      setAccountSubTab('system-admin')
      if (!isAuthenticated) {
        openAuthModal()
        return
      }
      setActiveTab('account')
      if (activeTab !== 'account') writeTabToUrl('account')
      setRawSearchResults([])
      setRawSearchQuery('')
      return
    }
    if (isAuthRequiredTab(tab) && !isAuthenticated && !isWorkingGuest) {
      openAuthModal()
      return
    }
    setActiveTab(tab)
    if (activeTab !== tab) writeTabToUrl(tab)
    setRawSearchResults([])
    setRawSearchQuery('')
  }

  // Fetch raw search results from /rag/search
  async function fetchRawSearchResults(query) {
    if (!query.trim()) {
      setRawSearchResults([])
      setRawSearchQuery('')
      return
    }
    
    try {
      const res = await fetch(`/rag/search?q=${encodeURIComponent(query)}&k=8`)
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`)
      setRawSearchResults(data.hits || [])
      setRawSearchQuery(query)
    } catch (e) {
      console.error('Error fetching raw search results:', e)
      setRawSearchResults([])
      setRawSearchQuery('')
    }
  }

  // When opening the agent window, default scroll to bottom so user sees most recent chat history (before paint).
  useLayoutEffect(() => {
    if (!chatDrawerOpen || disableChatScroll) return
    const messagesEl = messagesContainerRef.current
    if (messagesEl && typeof messagesEl.scrollTo === 'function') {
      messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: 'auto' })
    }
  }, [chatDrawerOpen, messages, disableChatScroll])

  // On mobile, layout/scrollHeight can be wrong until after the drawer open animation (250ms) and reflow.
  // When the drawer opens, scroll to bottom after animation + reflow so the user sees the latest messages.
  const prevChatDrawerOpenRef = useRef(chatDrawerOpen)
  useEffect(() => {
    if (disableChatScroll) return
    const justOpened = chatDrawerOpen && !prevChatDrawerOpenRef.current
    prevChatDrawerOpenRef.current = chatDrawerOpen
    if (!justOpened) return
    let cancelled = false
    const scrollToBottom = () => {
      const messagesEl = messagesContainerRef.current
      if (cancelled || !messagesEl || typeof messagesEl.scrollTo !== 'function') return
      messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: 'auto' })
    }
    const t1 = setTimeout(scrollToBottom, 300)  // after drawer animation (250ms)
    const t2 = setTimeout(scrollToBottom, 600)  // fallback for slow mobile layout
    return () => {
      cancelled = true
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [chatDrawerOpen, disableChatScroll])

  // Auto-scroll chat messages to bottom when drawer is open (sending/streaming).
  useEffect(() => {
    if (!chatDrawerOpen) return
    if (disableChatScroll) {
      if (chatDrawerOpen && !devLogOnceRef.current.scroll) {
        devLogOnceRef.current.scroll = true
        console.log('[dev] Chat scroll-to-bottom DISABLED. Use ?disableChatScroll=0 or remove sessionStorage to re-enable.')
      }
      return
    }
    let cancelled = false
    const scrollToBottom = (behavior = 'smooth') => {
      const messagesEl = messagesContainerRef.current
      if (cancelled || !messagesEl || typeof messagesEl.scrollTo !== 'function') return
      messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior })
    }
    const ensureInView = () => {
      const messagesEl = messagesContainerRef.current
      if (cancelled || !messagesEl || typeof messagesEl.scrollIntoView !== 'function') return
      messagesEl.scrollIntoView({ block: 'nearest', behavior: 'auto', inline: 'nearest' })
    }
    scrollToBottom('smooth')
    ensureInView()
    const id1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToBottom('smooth')
        ensureInView()
      })
    })
    const t1 = setTimeout(() => { scrollToBottom('auto'); ensureInView() }, 150)
    const t2 = setTimeout(() => { scrollToBottom('auto'); ensureInView() }, 400)
    const t3 = setTimeout(() => { scrollToBottom('auto'); ensureInView() }, 700)  // extra for mobile
    return () => {
      cancelled = true
      cancelAnimationFrame(id1)
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
    }
  }, [messages, chatDrawerOpen, disableChatScroll])

  // Show/hide scroll-to-bottom button based on scroll position in chat
  useEffect(() => {
    const el = messagesContainerRef.current
    if (!el) return
    const handleScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
      setShowScrollToBottom(distanceFromBottom > 80)
    }
    el.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => el.removeEventListener('scroll', handleScroll)
  }, [chatDrawerOpen, messages])

  // Header always visible when using drawer (no hide-on-scroll for drawer)
  useEffect(() => {
    if (!chatDrawerOpen) return
    setHeaderVisible(true)
  }, [chatDrawerOpen])

  // When drawer opens with a pending message (e.g. from Recipes featured prompt), send it after a short delay.
  // featuredPromptSendNonce ensures a click still schedules send when the drawer was already open (setChatDrawerOpen(true) is a no-op then).
  useEffect(() => {
    if (!chatDrawerOpen) return
    const message = pendingChatMessageRef.current
    const inputText = (() => {
      if (!message) return ''
      if (typeof message === 'string') return message
      if (typeof message === 'object') return String(message.input || message.displayText || '')
      return ''
    })()
    if (!inputText.trim()) return
    pendingChatMessageRef.current = null
    const t = setTimeout(() => {
      sendMessage(message)
    }, 200)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sendMessage omitted so effect does not re-run and cancel the timeout
  }, [chatDrawerOpen, featuredPromptSendNonce])

  // Recipe Detail / Cook Mode: open chat with a recipe-scoped question prefilled.
  useEffect(() => {
    function onCookAskQuestion(event) {
      const { question, recipeTitle, recipeId, surface } = event.detail || {}
      const q = typeof question === 'string' ? question.trim() : ''
      if (!q) return
      if (!isAuthenticated) {
        promptLockedAsk()
        return
      }
      const title = typeof recipeTitle === 'string' ? recipeTitle.trim() : ''
      const id = typeof recipeId === 'string' ? recipeId.trim() : ''
      const askSurface = surface === 'cook' || surface === 'detail' ? surface : ''
      const input = formatRecipeAskMessage(q, title, id)
      pendingChatMessageRef.current = {
        input,
        displayText: q,
        ...(id
          ? {
              recipeId: id,
              telemetryContext: {
                intent: 'recipe_ask_chip',
                entrypoint: 'recipe_ask_chip',
                entrypoint_id: id,
                ...(askSurface ? { surface: askSurface } : {}),
              },
            }
          : {}),
      }
      setChatDrawerOpen(true)
      setFeaturedPromptSendNonce((n) => n + 1)
    }
    window.addEventListener('cookAskQuestion', onCookAskQuestion)
    return () => window.removeEventListener('cookAskQuestion', onCookAskQuestion)
  }, [isAuthenticated, promptLockedAsk])

  const handleRecipesFeaturedPromptSelect = useCallback((message) => {
    if (!isAuthenticated) {
      promptLockedAsk()
      return
    }
    // Kid "Meals that … likes" is a local smart filter only (SavedListsManager toggles state).
    // Older deployed bundles still routed this through here; ignore so we never open chat for it.
    if (
      message &&
      typeof message === 'object' &&
      message.preferencesPrompt &&
      typeof message.preferencesPrompt === 'object' &&
      message.preferencesPrompt.type === 'kid_meals'
    ) {
      return
    }
    pendingChatMessageRef.current = message
    setChatDrawerOpen(true)
    setFeaturedPromptSendNonce((n) => n + 1)
  }, [isAuthenticated, promptLockedAsk])

  const handleAskLaunchSubmit = useCallback((query) => {
    const trimmed = String(query || '').trim()
    if (!trimmed) return
    if (!isAuthenticated) {
      promptLockedAsk()
      return
    }
    setAskLauncherOpen(false)
    pendingChatMessageRef.current = trimmed
    setChatDrawerOpen(true)
    setFeaturedPromptSendNonce((n) => n + 1)
  }, [isAuthenticated, promptLockedAsk])

  const handleAskLauncherToggle = useCallback(() => {
    if (authLoading) return
    if (!isAuthenticated) {
      promptLockedAsk(askNavAnchorRef.current)
      return
    }
    if (chatDrawerOpen) {
      setChatDrawerOpen(false)
      return
    }
    setAskLauncherOpen((open) => !open)
  }, [authLoading, isAuthenticated, promptLockedAsk, chatDrawerOpen])

  const closeAskLauncher = useCallback(() => {
    setAskLauncherOpen(false)
  }, [])

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_LAYOUT_BREAKPOINT - 1}px)`)
    const handler = () => setIsMobileViewport(mql.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  useEffect(() => {
    function onKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault()
        if (authLoading || setupFocusActive || returningLoginFocused) return
        if (!isAuthenticated) {
          promptLockedAsk()
          return
        }
        if (chatDrawerOpen) {
          setChatDrawerOpen(false)
          return
        }
        setAskLauncherOpen(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [authLoading, setupFocusActive, returningLoginFocused, isAuthenticated, promptLockedAsk, chatDrawerOpen])

  const handleTodayCookShortcut = useCallback(() => {
    if (!todayCookTarget?.recipeId) return
    hasUserSelectedTabRef.current = true
    setChatDrawerOpen(false)
    setAskLauncherOpen(false)
    writeRecipeUrl({
      tab: 'cook',
      id: todayCookTarget.recipeId,
      planItemId: todayCookTarget.planItemId,
    })
    setActiveTab('cook')
  }, [todayCookTarget?.planItemId, todayCookTarget?.recipeId])

  function formatTime(ts) {
    try {
      const d = new Date(ts)
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } catch {
      return ''
    }
  }


  function clearChat() {
    if (import.meta.env.DEV) {
      console.log('[dev] clearChat() called – this is the only place that sets messages to []. Stack trace:', new Error().stack)
    }
    setMessages([])
    setFeedbackDraft({})
    localStorage.removeItem('agent_messages')
  }

  async function submitFeedback(messageIndex, draft) {
    const m = messages[messageIndex]
    if (!draft?.rating) return
    if (!m?.response_activity_id) {
      setFeedbackSubmitError('Feedback is unavailable for this message (missing response id). Please send a new message and try again.')
      return
    }
    setFeedbackSubmitError(null)
    setFeedbackSubmittingIndex(messageIndex)
    try {
      const res = await authenticatedFetch('/feedback/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          response_activity_id: m.response_activity_id,
          rating: draft.rating,
          comment: draft.comment?.trim() || null,
          task_completed: draft.taskCompleted ?? null
        })
      })
      if (!res.ok) {
        const text = await res.text()
        let msg = `Failed to send feedback (${res.status})`
        try {
          const err = text ? JSON.parse(text) : {}
          const detail = err.detail ?? err.message ?? err.error
          const str = typeof detail === 'object' && detail !== null ? (detail.message ?? detail.detail ?? JSON.stringify(detail)) : detail
          if (str) msg = Array.isArray(str) ? str.join(' ') : String(str)
          else if (res.status === 401) msg = 'Please log in again to send feedback.'
          else if (res.status === 404) msg = 'That response could not be found. Try sending a new message first.'
        } catch {
          if (res.status === 401) msg = 'Please log in again to send feedback.'
        }
        setFeedbackSubmitError(msg)
        setError(msg)
        return
      }
      setMessages(prev => {
        const next = prev.slice()
        next[messageIndex] = { ...next[messageIndex], feedbackSent: true }
        return next
      })
      setFeedbackDraft(prev => {
        const next = { ...prev }
        delete next[messageIndex]
        return next
      })
    } catch (e) {
      const raw = e?.message || String(e)
      const isNetwork = !raw || /failed to fetch|network error|load failed/i.test(raw)
      const msg = isNetwork ? 'Network error. Check that the backend is running and try again.' : raw
      setFeedbackSubmitError(msg)
      setError(msg)
    } finally {
      setFeedbackSubmittingIndex(null)
    }
  }

  function parseRecipeImportIntent(text, fallbackUrl) {
    const lower = text.toLowerCase()
    
    // Check for question words - if it's a question, it's not an import intent
    const isQuestion = /^(which|what|where|when|who|how|why|do|does|did|can|could|will|would|is|are|was|were)\s+/i.test(text.trim())
    if (isQuestion) {
      return null
    }
    
    // Check for possessive/plural forms that indicate asking about existing recipes
    const isAskingAboutSaved = /(my|your|our|their|the)\s+(saved\s+)?recipes?/i.test(text) || 
                               /(which|what)\s+(of\s+)?(my|your|our|their|the)\s+(saved\s+)?recipes?/i.test(text) ||
                               /recipes?\s+(have|has|contain|includes?|with)/i.test(text)
    if (isAskingAboutSaved) {
      return null
    }
    
    // More specific import patterns - require clearer intent
    const wantsImport = /(import|save|add)\s+(this\s+)?recipe\b|find\s+recipe\b|recipe\s+from\s+web|import\s+recipe\s+from/i.test(lower)
    // URL
    const urlMatch = text.match(/https?:\/\/\S+/)
    const implicitImport = wantsImport || /import\s+(this|that|recipe)/i.test(lower)
    // Extract simple title by quotes
    const titleMatch = text.match(/[“"']([^"'”]{3,200})["”']/)
    // Author cues
    const byMatch = text.match(/by\s+([\w .'-]{2,100})/i)
    const useUrl = urlMatch ? urlMatch[0] : (implicitImport ? (fallbackUrl || '') : '')
    if (!wantsImport && !useUrl) return null
    
    // Extract title with fallback
    let title = ''
    if (titleMatch) {
      title = titleMatch[1].trim()
    } else if (wantsImport) {
      // Clean text to extract title
      title = text.replace(/import|save|add|recipe|please|from web|this|that/gi, '').trim()
      // Remove author "by X" if present
      if (byMatch) {
        title = title.replace(/by\s+[\w .'-]+/i, '').trim()
      }
    }
    
    // If still empty and we have a URL, use a generic title
    if (!title || title.length < 2) {
      if (useUrl) {
        // Extract domain or use generic
        try {
          const urlObj = new URL(useUrl)
          const hostname = urlObj.hostname.replace(/^www\./, '')
          title = `Recipe from ${hostname}`
        } catch {
          title = 'Imported Recipe'
        }
      } else {
        title = 'Imported Recipe'
      }
    }
    
    // Enforce min_length=2 and max_length=255
    title = title.trim()
    if (title.length < 2) title = 'Imported Recipe'
    if (title.length > 255) title = title.substring(0, 252) + '...'
    
    const payload = {
      title,
      author: byMatch ? byMatch[1].trim() : undefined,
      url: useUrl || undefined,
      include_pantry_items: false,
      tags: []
    }
    
    // Must have either url or valid title (min 2 chars)
    if (!payload.url && (!payload.title || payload.title.length < 2)) return null
    return payload
  }

  async function rememberServingChoice(action, recipeId, responseActivityId) {
    if (!recipeId) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Open the recipe and ask again so I can remember that choice.',
        ts: Date.now(),
      }])
      return
    }
    const result = await recordServingChoice(recipeId, action, authenticatedFetch, responseActivityId)
    setMessages(prev => [...prev, { role: 'assistant', text: result.message, ts: Date.now() }])
    if (result.shoppingListId) {
      try { localStorage.setItem('pending_chat_saved_list_id', result.shoppingListId) } catch { /* ignore */ }
      window.dispatchEvent(new CustomEvent('loadSavedList', { detail: { listId: result.shoppingListId } }))
    }
  }

  async function sendMessage(messageOrText) {
    const isObject = messageOrText && typeof messageOrText === 'object'
    const visibleText = isObject ? String(messageOrText.displayText || '') : String(messageOrText || '')
    const inputText = isObject ? String(messageOrText.input || messageOrText.displayText || '') : String(messageOrText || '')
    const preferencesPrompt = isObject ? (messageOrText.preferencesPrompt || null) : null
    const messageTelemetryContext = isObject ? (messageOrText.telemetryContext || null) : null
    const messageRecipeId = isObject ? String(messageOrText.recipeId || '').trim() : ''

    if (!inputText.trim() || loading) return
    
    // Check authentication before sending message
    if (!isAuthenticated) {
      promptLockedAsk()
      return
    }
    
    // Create abort controller for this request
    const controller = new AbortController()
    setAbortController(controller)
    
    // Only treat as clarification when we're actually expecting a follow-up answer
    // (e.g. "decide what to cook" who/time/cuisine). Generic low-confidence
    // responses do not set session_state, so the next message is a fresh request.
    const prevMessages = messages
    const lastAssistantMsg = prevMessages.filter(m => m.role === 'assistant').pop()
    const isClarificationResponse = lastAssistantMsg?.session_state === 'awaiting_clarification'
    
    const userMsg = { role: 'user', text: visibleText.trim() ? visibleText : inputText, ts: Date.now() }
    setMessages(prev => [...prev, userMsg])
    setLoading(true); setError('')
    
    try {
      // 1) Intercept recipe import intent
      const intent = parseRecipeImportIntent(inputText, lastAssistantUrl)
      if (intent) {
        setMessages(prev => [...prev, { role: 'assistant', text: '🔎 Importing recipe... searching/extracting...', ts: Date.now() }])
        try {
          const outcome = await importRecipeFromUrl(intent, (progress, msg) => {
            if (msg) {
              setMessages(prev => {
                const last = prev[prev.length - 1]
                if (last && last.role === 'assistant') {
                  return [...prev.slice(0, -1), { ...last, text: `⏳ Processing... ${msg} (${progress}%)` }]
                }
                return prev
              })
            }
          })
          if (outcome.success) {
            const result = outcome.result || {}
            const reimport = formatRecipeReimportMessage(result)
            const msg = result.status === 'updated'
              ? `✅ ${reimport.title}\n\n${reimport.message}\n📝 List: "${result.name}"\n📦 Items: ${result.item_count}\n🔗 Source: ${result.source_url || 'n/a'}`
              : result.status === 'deduplicated'
                ? `✅ ${reimport.title}\n\n${reimport.message}\n📝 List: "${result.name}"\n📦 Items: ${result.item_count}\n🔗 Source: ${result.source_url || 'n/a'}`
                : `✅ Recipe imported successfully!\n\n📝 List: "${result.name}"\n📦 Items: ${result.item_count}\n🔗 Source: ${result.source_url || 'n/a'}`
            setMessages(prev => [...prev, { role: 'assistant', text: msg, ts: Date.now() }])
            setTimeout(() => {
              setShortcutRefreshTrigger(prev => prev + 1)
            }, 1000)
          } else {
            const errorMsg = outcome.error || 'Unknown error'
            const isNotAllowed = errorMsg.includes('❌ Recipe Import Not Allowed') || errorMsg.includes('❌ Recipe Import Blocked')
            const isRecipeSiteAuth = isRecipeSiteAuthErrorMsg(errorMsg)
            const recipeSiteDomain = parseRecipeSiteAuthDomain(errorMsg)
            if (outcome.requiresAuth) {
              openAuthModal()
            } else if (isRecipeSiteAuth) {
              const domainLabel = recipeSiteDomain || 'this recipe site'
              setMessages(prev => [...prev, {
                role: 'assistant',
                text: `🔒 **${domainLabel}** requires you to be logged in.\n\nYou can add your cookies without hunting for values:\n1. Go to the **Recipes** tab → **Add Recipe**\n2. Paste the same recipe URL and click **Import**\n3. When you see the message, click **Authenticate and Import**\n4. Follow the steps (open the site, run the script in the browser console, paste back and click Import)\n5. Try **Import** again`,
                ts: Date.now()
              }])
            } else if (isNotAllowed) {
              setMessages(prev => [...prev, { role: 'assistant', text: errorMsg, ts: Date.now() }])
            } else {
              setMessages(prev => [...prev, {
                role: 'assistant',
                text: `❌ Import failed: ${errorMsg}\n\nPossible reasons:\n• Could not find recipe on the page\n• Site is blocked or requires login\n• Recipe data is not in expected format\n• Network or server error`,
                ts: Date.now()
              }])
            }
          }
        } catch (e) {
          const errorMsg = e.message || 'Unknown error'
          const isAppAuthError = (errorMsg.includes('401') || errorMsg.includes('403') ||
            errorMsg.includes('Unauthorized') || errorMsg.includes('Forbidden') ||
            errorMsg.toLowerCase().includes('authentication required')) &&
            !isRecipeSiteAuthErrorMsg(errorMsg)
          if (isAppAuthError) {
            openAuthModal()
          } else if (isRecipeSiteAuthErrorMsg(errorMsg)) {
            const recipeSiteDomain = parseRecipeSiteAuthDomain(errorMsg)
            const domainLabel = recipeSiteDomain || 'this recipe site'
            setMessages(prev => [...prev, {
              role: 'assistant',
              text: `🔒 **${domainLabel}** requires you to be logged in.\n\nGo to **Recipes** → **Add Recipe**, paste the recipe URL, then use **Authenticate and Import** to add your cookies (no need to find cookie values manually). Then try Import again.`,
              ts: Date.now()
            }])
          } else {
            setMessages(prev => [...prev, {
              role: 'assistant',
              text: `❌ Import error: ${errorMsg}\n\nPlease try:\n• Checking your internet connection\n• Verifying the recipe URL\n• Trying again in a moment`,
              ts: Date.now()
            }])
          }
        } finally {
          setLoading(false)
          setAbortController(null)
        }
        return
      }

      // 2) Fallback to normal chat
      // Add thinking message
      const thinkingMsgId = Date.now()
      setMessages(prev => [...prev, { role: 'assistant', text: '', thinking: true, ts: thinkingMsgId }])
      
      // Build request body with optional shortcut metadata and store context
      const requestBody = { input: inputText }
      if (preferencesPrompt && typeof preferencesPrompt === 'object') {
        requestBody.preferences_prompt = preferencesPrompt
      }
      if (messageTelemetryContext && typeof messageTelemetryContext === 'object') {
        requestBody.telemetry_context = messageTelemetryContext
      }
      // Stable client-side message id so backend can de-dupe / link analytics reliably
      const clientMessageId = (() => {
        try {
          if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
        } catch { /* ignore */ }
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`
      })()
      requestBody.client_message_id = clientMessageId
      // Always include session_id once established so the backend can provide
      // conversation continuity for follow-ups (not just clarification flows).
      if (currentSessionId) {
        requestBody.session_id = currentSessionId
      }
      if (currentShortcutPhrase) {
        requestBody.shortcut_phrase = currentShortcutPhrase
        if (!requestBody.telemetry_context) {
          requestBody.telemetry_context = {
            intent: 'chat_shortcut',
            entrypoint: 'chat_shortcut',
            entrypoint_id: String(currentShortcutPhrase).slice(0, 128),
          }
        }
      }
      if (selectedStore?.id) {
        requestBody.store_id = selectedStore.id
      }
      try {
        const activeSavedListId = localStorage.getItem('active_saved_list_id')
        const activeSavedListIsRecipeRaw = localStorage.getItem('active_saved_list_is_recipe')
        const activeCartId = localStorage.getItem('active_cart_id')
        if (activeSavedListId) {
          requestBody.current_saved_list_id = activeSavedListId
        }
        if (activeSavedListIsRecipeRaw != null) {
          requestBody.current_saved_list_is_recipe = activeSavedListIsRecipeRaw === 'true'
        }
        if (activeCartId) {
          requestBody.current_cart_id = activeCartId
          requestBody.telemetry_context = {
            ...(requestBody.telemetry_context || {}),
            cart_id: activeCartId,
            cart_model: 'canonical_dual_write',
          }
        }
      } catch {
        // Ignore errors reading from localStorage
      }
      // Recipe Detail / Cook Mode ask chips: pin the viewed recipe for this turn
      // (do not rely on Shop localStorage, which may be a grocery list).
      if (messageRecipeId) {
        requestBody.current_saved_list_id = messageRecipeId
        requestBody.current_saved_list_is_recipe = true
        requestBody.telemetry_context = {
          ...(requestBody.telemetry_context || {}),
          intent: 'recipe_ask_chip',
          entrypoint: 'recipe_ask_chip',
          entrypoint_id: messageRecipeId,
        }
      }
      // Include clarification response flags if this is a response to a clarification question
      if (isClarificationResponse && currentSessionId) {
        requestBody.is_clarification = true
        requestBody.session_id = currentSessionId
      }
      requestBody.stream = true
      const pinnedRecipeId = requestBody.current_saved_list_is_recipe
        ? String(requestBody.current_saved_list_id || '')
        : ''

      const res = await authenticatedFetch('/ask', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal  // Add abort signal
      })
      
      // Clear shortcut phrase after sending
      setCurrentShortcutPhrase(null)

      if (!res.ok) {
        const errText = await res.text()
        let errData = {}
        try { errData = errText ? JSON.parse(errText) : {} } catch { /* ignore */ }
        if (res.status === 408) throw new Error("⏰ Request timed out. The agent took too long to respond. Please try a simpler question or break it into smaller parts.")
        if (res.status === 401 || res.status === 403) {
          setMessages(prev => prev.filter(m => !m.thinking))
          openAuthModal()
          setLoading(false)
          setAbortController(null)
          return
        }
        throw new Error(errData.detail || `HTTP ${res.status}`)
      }

      const contentType = res.headers.get('content-type') || ''
      const isStream = contentType.includes('ndjson') || contentType.includes('x-ndjson')
      if (isStream && res.body) {
        const ts = Date.now()
        let buffer = ''; let fullText = ''
        const reader = res.body.getReader(); const decoder = new TextDecoder()
        try {
          while (true) {
            const { value, done } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n'); buffer = lines.pop() || ''
            for (const line of lines) {
              if (!line.trim()) continue
              try {
                const chunk = JSON.parse(line)
                if (chunk.type === 'token' && chunk.content) {
                  fullText += chunk.content
                  setMessages(curr => {
                    if (!curr.length) return curr
                    const next = curr.slice()
                    const idx = next.findIndex(m => m.thinking && m.ts === thinkingMsgId)
                    const lastIdx = next.length - 1
                    const targetIdx = idx !== -1 ? idx : lastIdx
                    const m = next[targetIdx]
                    const updated = { ...m, text: fullText, thinking: false, ts: m.ts || ts }
                    next[targetIdx] = updated
                    return next
                  })
                } else if (chunk.type === 'done') {
                  const sessionId = chunk.session_id || currentSessionId
                  const chatSavedListId = chunk.chat_saved_list_id || null
                  if (sessionId && sessionId !== currentSessionId) setCurrentSessionId(sessionId)
                  // Prefer done.reply (markers already stripped server-side) when present
                  const rawDisplay = (chunk.reply != null && String(chunk.reply).trim())
                    ? String(chunk.reply)
                    : ((fullText && fullText.trim()) ? fullText : '')
                  const emptyResponseMsg = "No response was received. The request may have timed out or the agent couldn't generate a reply. Please try again or rephrase your question."
                  const { shopListId, cleanedText } = extractShopListIdAndCleanText(rawDisplay)
                  const displayText = cleanedText.trim() ? cleanedText : emptyResponseMsg
                  setMessages(curr => (!curr.length ? curr : curr.slice().map((m, i) => i === curr.length - 1 ? {
                    ...m,
                    text: displayText,
                    thinking: false,
                    explainability: chunk.explainability ?? m.explainability,
                    response_activity_id: chunk.response_activity_id ?? m.response_activity_id,
                    session_id: sessionId,
                    // Parity with non-streaming /ask: clarification follow-ups need session_state on the message
                    needs_clarification: 'needs_clarification' in chunk ? chunk.needs_clarification : m.needs_clarification,
                    session_state: 'session_state' in chunk ? chunk.session_state : m.session_state,
                    clarification_questions: 'clarification_questions' in chunk ? chunk.clarification_questions : m.clarification_questions,
                    suggested_actions: Array.isArray(chunk.suggested_actions)
                      ? chunk.suggested_actions
                      : m.suggested_actions,
                    recipe_id: pinnedRecipeId || m.recipe_id,
                  } : m)))
                  setLoading(false); setAbortController(null)
                  const listIdToLoad = chatSavedListId || shopListId
                  if (listIdToLoad) {
                    try { localStorage.setItem('pending_chat_saved_list_id', listIdToLoad) } catch { /* ignore */ }
                    window.dispatchEvent(new CustomEvent('loadSavedList', { detail: { listId: listIdToLoad } }))
                  }
                  try { const u = fullText.match(/https?:\/\/\S+/)?.[0]; if (u) setLastAssistantUrl(u) } catch { /* ignore */ }
                  setTimeout(() => setShortcutRefreshTrigger(prev => prev + 1), 3000)
                  if (isAuthenticated) setTimeout(() => refreshPreferencesSummary(), 2500)
                  return
                } else if (chunk.type === 'error') throw new Error(chunk.detail || 'Stream error')
              } catch (e) { if (e instanceof SyntaxError) continue; throw e }
            }
          }
          if (fullText.trim() === '') {
            const emptyResponseMsg = "No response was received. The request may have timed out or the agent couldn't generate a reply. Please try again or rephrase your question."
            setMessages(prev => [...prev.filter(m => !(m.thinking && m.ts === thinkingMsgId)), { role: 'assistant', text: emptyResponseMsg, ts: Date.now() }])
          }
          setLoading(false); setAbortController(null)
          setTimeout(() => setShortcutRefreshTrigger(prev => prev + 1), 1000)
        } catch (streamErr) {
          if (streamErr.name === 'AbortError') throw streamErr
          setMessages(prev => prev.filter(m => !m.thinking))
          setError(streamErr.message || 'Stream error')
          setLoading(false); setAbortController(null)
          setTimeout(() => setShortcutRefreshTrigger(prev => prev + 1), 1000)
        }
        return
      }

      const responseText = await res.text()
      let data
      try { data = responseText ? JSON.parse(responseText) : {} } catch {
        throw new Error(/^\s*</.test(responseText) ? 'Server returned an error page.' : 'Invalid response.')
      }

      // Typewriter effect - replace thinking message with actual response
      const rawReply = String(data.reply || '')
      const { shopListId, cleanedText } = extractShopListIdAndCleanText(rawReply)
      const full = cleanedText
      const chatSavedListId = data.chat_saved_list_id || null
      const ts = Date.now()
      const explainability = data.explainability || null
      
      // Store clarification state and session_id if present
      const needsClarification = data.needs_clarification === true
      const sessionState = data.session_state || null
      const clarificationQuestions = data.clarification_questions || null
      const suggestedActions = Array.isArray(data.suggested_actions)
        ? data.suggested_actions
        : null
      
      // Extract session_id from response (backend always returns it now)
      // Use it for future clarification responses
      const sessionId = data.session_id || currentSessionId
      if (sessionId && sessionId !== currentSessionId) {
        setCurrentSessionId(sessionId)
      }
      // Phase 1 learning: id of the response row for feedback (thumbs up/down)
      const responseActivityId = data.response_activity_id || null

      const emptyResponseMsg = "No response was received. The request may have timed out or the agent couldn't generate a reply. Please try again or rephrase your question."

      if (full.trim() === '') {
        setMessages(prev => [...prev.filter(m => !(m.thinking && m.ts === thinkingMsgId)), { role: 'assistant', text: emptyResponseMsg, ts }])
        setLoading(false)
        setAbortController(null)
        setTimeout(() => setShortcutRefreshTrigger(prev => prev + 1), 1000)
        if (isAuthenticated) setTimeout(() => refreshPreferencesSummary(), 2500)
        return
      }
      
      setMessages(prev => {
        // Replace the thinking message with the actual response
        const next = prev.slice()
        const thinkingIndex = next.findIndex(m => m.thinking && m.ts === thinkingMsgId)
        const assistantMsg = { 
          role: 'assistant', 
          text: '', 
          ts, 
          explainability,
          needs_clarification: needsClarification,
          session_state: sessionState,
          clarification_questions: clarificationQuestions,
          suggested_actions: suggestedActions,
          session_id: sessionId,
          response_activity_id: responseActivityId,
          recipe_id: pinnedRecipeId || undefined,
        }
        if (thinkingIndex !== -1) {
          next[thinkingIndex] = assistantMsg
        } else {
          next.push(assistantMsg)
        }
        return next
      })
      let i = 0
      clearInterval(typeRef.current.timer)
      typeRef.current.timer = setInterval(() => {
        i = Math.min(i + Math.ceil(full.length / 60) || 1, full.length)
        const slice = full.slice(0, i)
        setMessages(curr => {
          if (!curr.length) return curr
          const next = curr.slice()
          // Preserve explainability data when updating text
          const lastMsg = next[next.length - 1]
          next[next.length - 1] = { ...lastMsg, text: slice, explainability: lastMsg.explainability || explainability, response_activity_id: lastMsg.response_activity_id }
          return next
        })
        if (i >= full.length) {
          clearInterval(typeRef.current.timer)
          setLoading(false)
          setAbortController(null)
          const listIdToLoad = chatSavedListId || shopListId
          if (listIdToLoad) {
            try { localStorage.setItem('pending_chat_saved_list_id', listIdToLoad) } catch { /* ignore */ }
            window.dispatchEvent(new CustomEvent('loadSavedList', { detail: { listId: listIdToLoad } }))
          }
          // After full message, capture first URL for quick-follow imports
          try {
            const urlInReply = full.match(/https?:\/\/\S+/)?.[0]
            if (urlInReply) setLastAssistantUrl(urlInReply)
          } catch {
            // Ignore errors when extracting URL
          }
          // Trigger shortcut refresh after message completes (with delay to allow backend to process)
          // Increased delay to ensure query is logged and response is marked as successful
          setTimeout(() => {
            setShortcutRefreshTrigger(prev => prev + 1)
          }, 3000) // 3 second delay to allow backend to log query and process response

          // Preference extraction runs in the backend; refresh availability after a short delay
          if (isAuthenticated) {
            setTimeout(() => {
              refreshPreferencesSummary()
            }, 2500)
          }
        }
      }, 16)
    } catch (e) {
      // Remove thinking message on error
      setMessages(prev => prev.filter(m => !m.thinking))
      
      if (e.name === 'AbortError') {
        setError('Request cancelled by user')
        // Keep user message, just remove thinking message (already done above)
      } else {
        const rawMsg = String(e.message || e)
        // Browser network errors (request never reached server): Safari "An error occurred trying to load the resource", Chrome "Failed to fetch"
        const isNetworkError = !rawMsg || rawMsg.includes('load the resource') || rawMsg.includes('Failed to fetch') ||
          rawMsg.includes('NetworkError') || rawMsg.includes('Network request failed') || rawMsg.includes('Load failed')
        const errorMsg = isNetworkError
          ? 'No reply just now. Try again.'
          : rawMsg
        // Check if this is an authentication error (401 or 403) using raw message
        const isAuthError = rawMsg.includes('401') || rawMsg.includes('403') ||
                           rawMsg.includes('Unauthorized') || rawMsg.includes('Forbidden') ||
                           rawMsg.toLowerCase().includes('authentication required')
        
        if (isAuthError) {
          // Show login modal instead of adding error to chat
          openAuthModal()
        } else {
          setError(errorMsg)
          // Add error message
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            text: `❌ Error: ${errorMsg}`, 
            ts: Date.now() 
          }])
        }
      }
      setLoading(false)
      setAbortController(null)
      // Trigger shortcut refresh even on error (query was still logged)
      setTimeout(() => {
        setShortcutRefreshTrigger(prev => prev + 1)
      }, 1000)
    }
  }

  function cancelRequest() {
    if (abortController) {
      abortController.abort()
      setAbortController(null)
    }
  }


  return (
    <div
      className={[
        'app-container',
        guestAuthGateActive ? 'guest-auth-gate-active' : '',
        isWorkingGuest && guestSaveStatus === 'offline' ? 'guest-offline-readonly' : '',
        setupFocusActive ? 'setup-focused' : '',
      ].filter(Boolean).join(' ')}
    >
      {/* Skip Links for Keyboard Navigation */}
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <a href="#navigation" className="skip-link">Skip to navigation</a>
      
      <TopNav
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onLoginClick={openLoginModal}
        chatOpen={chatDrawerOpen}
        askLauncherOpen={askLauncherOpen}
        onAskLauncherToggle={handleAskLauncherToggle}
        askNavAnchorRef={askNavAnchorRef}
        todayCookTarget={todayCookShortcutTarget}
        onTodayCookClick={handleTodayCookShortcut}
        navigationLocked={navigationLocked}
        navigationLockReason={
          guestOnboardingActive
            ? guestSetupLockReason(guestOnboardingStep)
            : undefined
        }
        navigationQuiet={guestOnboardingActive}
        hideMainNavigation={setupFocusActive || returningLoginFocused}
        hideAsk={setupFocusActive || returningLoginFocused}
        hideLogin={returningLoginFocused}
        onBrandClick={isWorkingGuest ? openGuestHome : undefined}
      />
      {isWorkingGuest && !guestHomeOpen ? (
        <GuestKitchenStrip
          status={guestSaveStatus}
          setup={setupFocusActive && guestSaveStatus === 'idle'}
          onSave={openKeepDialog}
          onRetry={retryGuestSave}
          saveEnabled={!setupFocusActive}
        />
      ) : null}

      <AskTheCookLauncher
        open={askLauncherOpen}
        onClose={closeAskLauncher}
        onSubmit={handleAskLaunchSubmit}
        anchorRef={askNavAnchorRef}
        variant={isMobileViewport ? 'sheet' : 'dropdown'}
      />

      <AskLockedHint
        open={lockedAskHintOpen}
        onClose={closeLockedAskHint}
        anchorRef={lockedAskAnchorRef}
      />

      {/* Main Content Area - only active tab panel is in the DOM (no display:none) */}
      <main
        id="main-content"
        ref={mainContentRef}
        className="main-content"
        data-active-tab={activeTab}
        data-scrolling={isMainContentScrolling ? '' : undefined}
      >
        {showLoggedOutHome ? (
          <HomePage
            cohort={cohort}
            snapshot={readAccountSnapshot()}
            startStatus={startStatus}
            onStart={handleStartGuest}
            onLogin={openLoginModal}
            onLoginSuccess={finishAuthLogin}
            actionLabel={guestKitchen ? 'Back to my kitchen' : 'Start my kitchen'}
            mobile={isMobileViewport}
          />
        ) : showPrivacyView ? (
          <PrivacyPolicyView onClose={() => setShowPrivacyView(false)} />
        ) : (
        <>
        {isAuthenticated && keepResult ? (
          <div className="tab-content-wrapper keep-saved-wrap">
            <KeepSavedCard
              result={keepResult}
              status={keepResultStatus}
              onRetry={attachGuestKitchen}
              onDismiss={handleKeepDismiss}
            />
          </div>
        ) : null}
        {/* Recipe Detail — /cook?tab=recipe&id=:recipeId */}
        {activeTab === 'recipe' && (() => {
          const { id } = readRecipeParams(window.location.search)
          return (
            <Suspense fallback={<RecipeDetailSkeleton />}>
              <RecipeDetail
                recipeId={id}
                selectedStoreId={selectedStore?.id}
                onNavigate={({ tab, id: nId, session: nSession, replace }) => {
                  if (tab === 'shop') {
                    handleTabChange('shop')
                    return
                  }
                  writeRecipeUrl({ tab, id: nId, session: nSession }, { replace })
                  setActiveTab(tab)
                }}
                onGoBack={() => {
                  writeTabToUrl('recipes')
                  setActiveTab('recipes')
                }}
              />
            </Suspense>
          )
        })()}

        {/* Cook Mode — /cook?tab=cook&id=:recipeId&session=:sessionId */}
        {activeTab === 'cook' && (() => {
          const { id, session, planItemId } = readRecipeParams(window.location.search)
          return (
            <Suspense fallback={<RecipeScreenLoading label="Loading cook mode…" />}>
              <CookMode
                recipeId={id}
                sessionId={session}
                planItemId={planItemId}
                onNavigate={({ tab, id: nId, session: nSession, planItemId: nPlanItemId, replace }) => {
                  writeRecipeUrl({ tab, id: nId, session: nSession, planItemId: nPlanItemId }, { replace })
                  setActiveTab(tab)
                }}
                onExit={() => {
                  if (id) {
                    writeRecipeUrl({ tab: 'recipe', id })
                    setActiveTab('recipe')
                  } else {
                    writeTabToUrl('recipes')
                    setActiveTab('recipes')
                  }
                }}
              />
            </Suspense>
          )
        })()}

        {/* Shop Tab: Item Lookup + Grocery List (no Saved Lists sub-tab) */}
        {activeTab === 'shop' && (
        <div 
          id="shop-panel" 
          className="tab-content shop-tab" 
          role="tabpanel" 
          aria-labelledby="shop-tab"
        >
          <ShopErrorBoundary>
            <GrocerySearch 
              guestMode={isWorkingGuest}
              setShowLoginModal={setShowLoginModal} 
              initialLoadList={currentLoadList}
              onClearPendingLoadList={() => {
                const next = loadListQueueRef.current.shift() ?? null
                setCurrentLoadList(next)
                setLoadListQueueLength(loadListQueueRef.current.length)
              }}
              pendingQueueLength={loadListQueueLength}
              pendingRecipeImageFile={pendingRecipeImageFile}
              onRecipeImageImportFlowComplete={() => setPendingRecipeImageFile(null)}
            />
          </ShopErrorBoundary>
        </div>
        )}

        {/* Recipes Tab */}
        {activeTab === 'recipes' && (
          <>
          {guestOnboardingActive && guestOnboardingStep === 2 ? (
            <GuestOnboardingProgress
              step={2}
              onSelectStep={advanceGuestOnboarding}
            />
          ) : null}
          <RecipesTabPanel
            guestMode={isWorkingGuest}
            footer={guestOnboardingActive && guestOnboardingStep === 2 ? (
              <OnboardingStepFooter
                label="Continue to Preferences"
                count={onboardingRecipeCount}
                disabled={onboardingRecipeCount < ONBOARDING_RECIPES_TO_CONTINUE}
                onClick={() => advanceGuestOnboarding(3)}
                status={onboardingRecipeProgressLabel(onboardingRecipeCount)}
              />
            ) : null}
            hidePageHeader={guestOnboardingActive && guestOnboardingStep === 2}
            hideGetRecipesNav={guestOnboardingActive && guestOnboardingStep === 2}
            defaultGetRecipesMethod={
              guestOnboardingActive && guestOnboardingStep === 2 ? 'add' : undefined
            }
            onLoadList={(items, listId, listName, options) => {
              const payload = { items, id: listId, name: listName, isRecipe: options?.isRecipe ?? false }
              loadListQueueRef.current.push(payload)
              setCurrentLoadList((prev) => {
                if (prev !== null) return prev
                return loadListQueueRef.current.shift() ?? null
              })
              setLoadListQueueLength(loadListQueueRef.current.length)
              setActiveTab('shop')
            }}
            setShowLoginModal={setShowLoginModal}
            onRecipeImageSelected={(file) => setPendingRecipeImageFile(file)}
            onFeaturedPromptSelect={handleRecipesFeaturedPromptSelect}
            showGoShoppingBanner={showGoShoppingBanner}
            onGoShopping={() => handleTabChange('shop')}
            onPantryShoppingBannerFromMatch={setShowGoShoppingBanner}
            onViewRecipe={(recipeId) => {
              writeRecipeUrl({ tab: 'recipe', id: recipeId })
              setActiveTab('recipe')
            }}
            onManagePreferences={() => handleTabChange('account', 'preferences')}
          />
          </>
        )}

        {/* Pantry Tab – only available when authenticated (tab hidden when logged out) */}
        {activeTab === 'pantry' && (
          <>
          {guestOnboardingActive && guestOnboardingStep === 1 ? (
            <GuestOnboardingProgress
              step={1}
              onSelectStep={advanceGuestOnboarding}
            />
          ) : null}
          <div
            id="pantry-panel"
            className="tab-content pantry-tab"
            role="tabpanel"
            aria-labelledby="pantry-tab"
          >
            <YourPantry
                guestMode={isWorkingGuest}
                showGoShoppingBanner={showGoShoppingBanner}
                onInventorySettled={handlePantryInventorySettled}
                onGoShopping={() => handleTabChange('shop')}
                openReceiptTab={pantryOpenReceipt}
                onReceiptTabOpened={() => setPantryOpenReceipt(false)}
                onGuestOnboardingContinue={
                  guestOnboardingActive && guestOnboardingStep === 1
                    ? () => advanceGuestOnboarding(2)
                    : undefined
                }
              />
          </div>
          </>
        )}

        {/* Plan Tab – Week Planner; authenticated only */}
        {activeTab === 'plan' && (
          <>
          {guestOnboardingActive && guestOnboardingStep === 4 ? (
            <GuestOnboardingProgress
              step={4}
              onSelectStep={advanceGuestOnboarding}
            />
          ) : null}
          <div
            id="plan-panel"
            className="tab-content plan-tab"
            role="tabpanel"
            aria-labelledby="plan-tab"
          >
            {isWorkingGuest && guestOnboardingActive && guestOnboardingStep < 4 ? (
              <GuestPlanSurface
                onboardingStep={guestOnboardingStep}
                onSelectStep={guestOnboardingActive && guestOnboardingStep === 3 ? advanceGuestOnboarding : undefined}
                onPreferencesContinue={() => advanceGuestOnboarding(4)}
              />
            ) : isWorkingGuest && guestOnboardingActive && guestOnboardingStep === 4 ? (
              <OnboardingPlan
                initializeEmptyWeek={!guestKitchen?.onboardingPlanInitialized}
                onInitialized={markOnboardingPlanInitialized}
                onSavePrompt={promptOnboardingSave}
                saveAskDismissed={Boolean(guestKitchen?.keepDismissed)}
                onSwitchToRecipes={() => handleTabChange('recipes')}
                onSwitchToShop={() => handleTabChange('shop')}
                onImportRecipe={() => {
                  handleTabChange('recipes')
                  window.setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('cookSetMethod', { detail: { method: 'add' } }))
                  }, 0)
                }}
                onOpenMainChat={() => {
                  setChatDrawerOpen(true)
                }}
              />
            ) : (
            <PlannerPanel
              key={keepResult?.mergeId || 'planner'}
              guestMode={isWorkingGuest || guestAttachPending}
              onSwitchToRecipes={() => handleTabChange('recipes')}
              onSwitchToShop={() => handleTabChange('shop')}
              onImportRecipe={() => {
                handleTabChange('recipes')
                // Defer: Cook panel is not mounted until after this handler; a sync dispatch
                // would miss SavedListsManager's cookSetMethod listener (defaults to Browse).
                window.setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('cookSetMethod', { detail: { method: 'add' } }))
                }, 0)
              }}
              onViewRecipe={(recipeId) => {
                writeRecipeUrl({ tab: 'recipe', id: recipeId })
                setActiveTab('recipe')
              }}
              onOpenMainChat={() => {
                setChatDrawerOpen(true)
              }}
            />
            )}
          </div>
          </>
        )}

        {/* Hidden GrocerySearch when recipe image selected from Recipes tab: runs upload/import without switching to Shop; modals and spinner portaled to body */}
        {activeTab === 'recipes' && pendingRecipeImageFile && (
        <div
          className="sr-offscreen-mount"
          aria-hidden="true"
        >
          <GrocerySearch
            setShowLoginModal={setShowLoginModal}
            initialLoadList={null}
            onClearPendingLoadList={() => {}}
            pendingQueueLength={0}
            pendingRecipeImageFile={pendingRecipeImageFile}
            onRecipeImageImportFlowComplete={() => setPendingRecipeImageFile(null)}
          />
        </div>
        )}

        <ChatDrawer
          open={chatDrawerOpen}
          onClose={() => setChatDrawerOpen(false)}
          isMobileViewport={isMobileViewport}
          anchorRef={askNavAnchorRef}
          onOpenHelp={() => setHelpModalOpen(true)}
          onClearChat={() => setShowClearChatConfirm(true)}
          showClearChat={messages.length > 0}
        >
          <div className={`chat-container chat-drawer-body ${messages.length ? 'has-messages' : 'empty'}`}>
            <div className="messages" ref={messagesContainerRef}>
              {!isAuthenticated && (
                <div className="card chat-auth-gate">
                  <div className="chat-auth-gate-icon-wrap">
                    <i className="fas fa-lock icon-xl" aria-hidden="true"></i>
                  </div>
                  <h2 className="chat-auth-gate-title">
                    {isNewVisitor
                      ? 'Create an account to get help from the Captain.'
                      : 'Log in to get help from the Captain.'}
                  </h2>
                  <p className="chat-auth-gate-desc">
                    The Captain can answer questions about recipes, pantry, and shopping once your kitchen is saved.
                  </p>
                  <button
                    onClick={openAuthModal}
                    className="primary chat-auth-gate-btn"
                    aria-label={isNewVisitor ? 'Create an account to ask the Captain' : 'Log in to ask the Captain'}
                  >
                    <i className="fas fa-sign-in-alt icon-sm icon-inverse" aria-hidden="true"></i>
                    {isNewVisitor ? 'Create an account' : 'Log in'}
                  </button>
                </div>
              )}
              {(() => {
                const lastAssistantIdx = messages.reduce(
                  (acc, msg, idx) =>
                    msg.role === 'assistant' && !msg.thinking ? idx : acc,
                  -1
                )
                return messages.map((m, i) => {
                const resolved =
                  m.role === 'assistant' && !m.thinking
                    ? resolveSuggestedActions(m)
                    : null
                const assistantDisplayText = resolved ? resolved.cleanedText : m.text
                const suggestedActions = resolved?.actions || []
                const isLatestAssistantWithActions =
                  suggestedActions.length > 0 && i === lastAssistantIdx

                return (
                <div key={i} className={`message ${m.role === 'user' ? 'user' : 'assistant'}`}>
                  <div className="bubble">
                    {m.thinking ? (
                      <div className="chat-thinking">
                        <i className="fas fa-brain icon-base icon-muted" aria-hidden="true"></i>
                        <span>Thinking...</span>
                      </div>
                    ) : m.role === 'assistant' ? (
                      <div className="chat-assistant-markdown">
                        <ReactMarkdown components={chatMarkdownComponents}>
                          {stableAssistantMarkdownSource(assistantDisplayText)}
                        </ReactMarkdown>
                        {isLatestAssistantWithActions && (
                          <div className="chat-suggested-actions" role="group" aria-label="Suggested replies">
                            {suggestedActions.map((action) => (
                              <button
                                key={action.id}
                                type="button"
                                className={`secondary chat-suggested-action-btn${action.kind === 'show_more' ? ' chat-suggested-action-btn--primary' : ''}`}
                                disabled={loading}
                                aria-label={action.label}
                                onClick={() => {
                                  if (action.kind === 'serving_choice') {
                                    rememberServingChoice(action, m.recipe_id, m.response_activity_id)
                                    return
                                  }
                                  sendMessage(action.sendText)
                                }}
                              >
                                {action.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="chat-user-text">{m.text}</div>
                    )}
                    {m.ts && !m.thinking && <div className="timestamp">{formatTime(m.ts)}</div>}
                    {/* Show raw search results button for user messages that triggered document queries */}
                    {m.role === 'user' && showRawResults && i === messages.length - 1 && (
                      <div className="chat-msg-raw-search">
                        <button
                          onClick={() => fetchRawSearchResults(m.text)}
                          className="secondary chat-msg-raw-search-btn"
                          aria-label="Show raw search results for this query"
                        >
                          <i className="fas fa-file-alt icon-sm icon-muted" aria-hidden="true"></i> Show Searched Files
                        </button>
                      </div>
                    )}
                    {m.role === 'assistant' && !m.thinking && m.explainability && (
                      <div className="chat-msg-explainability">
                        <ExplainabilityCard explainability={m.explainability} collapsed={false} />
                      </div>
                    )}
                    {m.role === 'assistant' && !m.thinking && (
                      <div className="chat-feedback-section">
                        {m.feedbackSent ? (
                          <div className="chat-feedback-thanks">
                            <i className="fas fa-check-circle" aria-hidden="true"></i>
                            <span>Thanks for your feedback.</span>
                          </div>
                        ) : (
                          <>
                            <div className="chat-feedback-row">
                              <span className="chat-feedback-label">Was this helpful?</span>
                              {!m.response_activity_id && (
                                <span className="chat-feedback-label">
                                  (feedback unavailable for this message)
                                </span>
                              )}
                              <button
                                type="button"
                                className="secondary chat-feedback-btn"
                                aria-label="Helpful"
                                disabled={!m.response_activity_id}
                                onClick={() => setFeedbackDraft(prev => ({ ...prev, [i]: { ...prev[i], rating: 'positive' } }))}
                              >
                                <i
                                  className={`fas fa-thumbs-up${feedbackDraft[i]?.rating === 'positive' ? ' chat-feedback-thumb--positive' : ''}`}
                                  aria-hidden="true"
                                ></i>
                              </button>
                              <button
                                type="button"
                                className="secondary chat-feedback-btn"
                                aria-label="Not helpful"
                                disabled={!m.response_activity_id}
                                onClick={() => setFeedbackDraft(prev => ({ ...prev, [i]: { ...prev[i], rating: 'negative' } }))}
                              >
                                <i
                                  className={`fas fa-thumbs-down${feedbackDraft[i]?.rating === 'negative' ? ' chat-feedback-thumb--negative' : ''}`}
                                  aria-hidden="true"
                                ></i>
                              </button>
                            </div>
                            {feedbackDraft[i]?.rating && (
                              <div className="chat-feedback-form">
                                <textarea
                                  className="input chat-feedback-textarea"
                                  placeholder="Optional: add a comment"
                                  value={feedbackDraft[i]?.comment ?? ''}
                                  onChange={e => setFeedbackDraft(prev => ({ ...prev, [i]: { ...prev[i], comment: e.target.value } }))}
                                  rows={2}
                                  aria-label="Optional comment"
                                />
                                <div className="chat-feedback-task-row">
                                  <span className="chat-feedback-label">Did this solve your task?</span>
                                  <button
                                    type="button"
                                    className={`chat-feedback-task-btn ${feedbackDraft[i]?.taskCompleted === true ? 'primary' : 'secondary'}`}
                                    onClick={() => setFeedbackDraft(prev => ({ ...prev, [i]: { ...prev[i], taskCompleted: true } }))}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    type="button"
                                    className={`chat-feedback-task-btn ${feedbackDraft[i]?.taskCompleted === false ? 'primary' : 'secondary'}`}
                                    onClick={() => setFeedbackDraft(prev => ({ ...prev, [i]: { ...prev[i], taskCompleted: false } }))}
                                  >
                                    No
                                  </button>
                                </div>
                                {feedbackSubmitError && (
                                  <div className="chat-feedback-error" role="alert">
                                    {feedbackSubmitError}
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className="primary chat-feedback-submit"
                                  disabled={feedbackSubmittingIndex === i || !m.response_activity_id}
                                  onClick={() => submitFeedback(i, feedbackDraft[i])}
                                >
                                  {feedbackSubmittingIndex === i ? 'Sending…' : 'Send feedback'}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                )
              })
              })()}
              {/* Raw Search Results Display */}
              {showRawResults && rawSearchResults.length > 0 && (
                <div className="message assistant">
                  <div className="bubble card chat-raw-results-bubble">
                    <div className="chat-raw-results-header">
                      <div className="chat-raw-results-title">
                        <i className="fas fa-file-alt icon-sm icon-primary" aria-hidden="true"></i> Searched Files ({rawSearchResults.length})
                      </div>
                      <button
                        onClick={() => {
                          setRawSearchResults([])
                          setRawSearchQuery('')
                        }}
                        className="secondary chat-raw-results-hide-btn"
                        aria-label="Hide raw search results"
                      >
                        <i className="fas fa-times icon-sm icon-muted" aria-hidden="true"></i> Hide
                      </button>
                    </div>
                    {rawSearchQuery && (
                      <div className="chat-raw-results-query">
                        Query: "{rawSearchQuery}"
                      </div>
                    )}
                    <div className="chat-raw-results-scroll">
                      <ul className="list chat-raw-results-list">
                        {rawSearchResults.map((hit, idx) => (
                          <li key={idx} className="list-item chat-raw-results-item">
                            <div className="chat-raw-results-item-header">
                              <div className="chat-raw-results-source">
                                {hit.source || 'Unknown source'}
                                {hit.chunk !== undefined && <span className="chat-raw-results-chunk"> #{hit.chunk}</span>}
                              </div>
                              {hit.preview_url && (
                                <a
                                  href={hit.preview_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="chat-raw-results-preview-link"
                                  title={`Preview ${hit.file_type || 'file'}`}
                                >
                                  <i className="fas fa-eye icon-sm icon-primary" aria-hidden="true"></i>
                                  Preview
                                </a>
                              )}
                            </div>
                            <div className="chat-raw-results-text">
                              {hit.text?.substring(0, 300)}
                              {hit.text && hit.text.length > 300 && '...'}
                            </div>
                            {hit.metadata && (
                              <div className="chat-raw-results-meta">
                                {hit.metadata.type === 'image' && <span><i className="fas fa-image icon-xs icon-muted" aria-hidden="true"></i> Image</span>}
                                {hit.file_type && hit.file_type !== 'image' && (
                                  <span><i className={`fas fa-file${hit.file_type === 'pdf' ? '-pdf' : hit.file_type === 'csv' ? '-csv' : '-alt'} icon-xs`} aria-hidden="true"></i> {hit.file_type.toUpperCase()}</span>
                                )}
                                {hit.metadata.ingest_source && <span> • Source: {hit.metadata.ingest_source}</span>}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              {showScrollToBottom && (
                <button
                  className="chat-scroll-to-bottom"
                  onClick={() => {
                    const el = messagesContainerRef.current
                    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
                  }}
                  aria-label="Scroll to bottom"
                >
                  <i className="fas fa-chevron-down" aria-hidden="true"></i>
                </button>
              )}
              <div ref={endRef} />
            </div>
            {isAuthenticated && (
              <div className="chat-composer-wrapper">
                {loading && abortController && (
                  <div className="chat-stop-request-wrap">
                    <button
                      type="button"
                      className="chat-stop-request-btn"
                      onClick={cancelRequest}
                      aria-label="Stop generating response"
                    >
                      <i className="fas fa-stop" aria-hidden="true" />
                      Stop
                    </button>
                  </div>
                )}
                <UnifiedSearch
                  panelEmbedded
                  onSendMessage={sendMessage}
                  loading={loading}
                  showRawResults={showRawResults}
                  onFetchRawResults={fetchRawSearchResults}
                  onShortcutSelect={setCurrentShortcutPhrase}
                  shortcutRefreshTrigger={shortcutRefreshTrigger}
                  hasMessages={messages.length > 0}
                  showChatShortcuts={enableChatShortcuts}
                  onClearChat={() => setShowClearChatConfirm(true)}
                />
              </div>
            )}
            {showClearChatConfirm && (
              <div
                className="chat-clear-confirm-overlay"
                onClick={() => setShowClearChatConfirm(false)}
                role="dialog"
                aria-modal="true"
                aria-label="Confirm clear chat"
              >
                <div
                  className="chat-clear-confirm"
                  onClick={e => e.stopPropagation()}
                >
                  <p>Clear all chat history? This can't be undone.</p>
                  <div className="chat-clear-confirm-actions">
                    <button onClick={() => setShowClearChatConfirm(false)}>
                      Cancel
                    </button>
                    <button
                      className="danger"
                      onClick={() => { clearChat(); setShowClearChatConfirm(false) }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ChatDrawer>

        {/* Account Tab – unified Profile + Preferences with internal sub-tab strip */}
        {activeTab === 'account' && (
          <div
            id="account-panel"
            className="tab-content account-tab"
            role="tabpanel"
            aria-label="Account"
          >
            <AccountPage
              subTab={accountSubTab}
              onSubTabChange={setAccountSubTab}
              onPreferencesChanged={handlePreferencesChanged}
              systemSubTab={systemSubTab}
              setSystemSubTab={setSystemSubTab}
              documentsContent={<DocumentsAdminPage />}
            />
          </div>
        )}

        <GlobalFooter
          onOpenPrivacy={() => setShowPrivacyView(true)}
          onOpenHelp={() => setHelpModalOpen(true)}
        />
        </>
        )}
      </main>

      {!setupFocusActive && !returningLoginFocused ? (
      <BottomTabBar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isAuthenticated={isAuthenticated}
        onRequireLogin={openAuthModal}
        guestActive={isWorkingGuest}
        navigationLocked={navigationLocked}
        navigationLockReason={
          guestOnboardingActive
            ? guestSetupLockReason(guestOnboardingStep)
            : undefined
        }
        navigationQuiet={guestOnboardingActive}
      />
      ) : null}

      {isMobileViewport && !showLoggedOutHome && (
        <AskTheCookFab
          locked={!isAuthenticated}
          onClick={(event) => {
            if (authLoading) return
            if (!isAuthenticated) {
              promptLockedAsk(event.currentTarget)
              return
            }
            if (chatDrawerOpen) {
              setChatDrawerOpen(false)
              return
            }
            setAskLauncherOpen(true)
          }}
          hidden={
            askLauncherOpen
            || chatDrawerOpen
            || activeTab === 'cook'
            || setupFocusActive
          }
        />
      )}

      <HelpModal isOpen={helpModalOpen} onClose={() => setHelpModalOpen(false)} />

      {isAuthenticated ? <PantryCleanupHost /> : null}

      <LoginModal
        isOpen={showLoginModal}
        onClose={handleLoginModalClose}
        onOpenPrivacy={handleOpenPrivacyFromAuth}
        initialMode={authModalMode}
        onboardingCopy={null}
        mobile={isMobileViewport}
        onLoginSuccess={finishAuthLogin}
      />

      <KeepKitchenDialog
        open={keepOpen}
        mobile={isMobileViewport}
        kitchen={guestKitchen}
        onRegistered={attachGuestKitchen}
        onLoginInstead={handleKeepLoginInstead}
        onNotNow={handleKeepNotNow}
        merging={mergeStatus === 'busy'}
      />

      <DiscardKitchenConfirm
        open={discardOpen}
        mobile={isMobileViewport}
        kitchen={guestKitchen}
        onConfirm={handleDiscardConfirm}
        onCancel={handleDiscardCancel}
      />
    </div>
  )
}

